import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  Switch,
  Textarea,
  useDisclosure
} from '@nextui-org/react';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { apiFetch, apiUpload } from '../lib/api';
import { extractJsonObject } from '../lib/json';
import { applySort } from '../lib/sort';
import { useAuth } from '../hooks/useAuth';
import { BannerViews } from '../lib/bannerViews';
import { PromoWhitelist } from '../lib/promoWhitelist';
import { shrinkToSurface } from '../lib/imageShrink';

// La lista blanca de un banner no tiene tope de entradas (hasta el
// 28-oct-2026 eran 500): su único límite es el de Firestore, 1 MiB por
// documento. Se mide el JSON de lo que se va a escribir, que da algo más que
// la cuenta de Firestore (comillas y comas), así que avisa un poco antes,
// nunca después. Devuelve cuántas entradas sobran (0 = cabe).
const FIRESTORE_DOC_MAX_BYTES = 1048576 - 8192;
const HASH_JSON_BYTES = 35; // 32 cifras, dos comillas y una coma

function jsonBytes(value) {
  try {
    return new TextEncoder().encode(JSON.stringify(value)).length;
  } catch (_) {
    return 0;
  }
}

function whitelistTooBig(payload, privateDoc) {
  const hashes = Array.isArray(payload?.audience?.whitelist) ? payload.audience.whitelist : [];
  const raw = Array.isArray(privateDoc?.whitelist) ? privateDoc.whitelist : [];
  let excess = 0;
  const docExtra = hashes.length ? jsonBytes(payload) - FIRESTORE_DOC_MAX_BYTES : 0;
  if (docExtra > 0) excess = Math.ceil(docExtra / HASH_JSON_BYTES);
  const privateExtra = raw.length ? jsonBytes(privateDoc) - FIRESTORE_DOC_MAX_BYTES : 0;
  if (privateExtra > 0) excess = Math.max(excess, Math.ceil(privateExtra / Math.max(1, jsonBytes(raw) / raw.length)));
  return Math.min(excess, Math.max(hashes.length, raw.length));
}

const TRANSLATION_LANGS = [
  { key: 'es', label: 'ES', name: 'Spanish' },
  { key: 'en', label: 'EN', name: 'English' },
  { key: 'fr', label: 'FR', name: 'French' },
  { key: 'de', label: 'DE', name: 'German' },
  { key: 'zh', label: 'ZH', name: 'Chinese' },
  { key: 'ja', label: 'JA', name: 'Japanese' },
  { key: 'hi', label: 'HI', name: 'Hindi' },
  { key: 'ru', label: 'RU', name: 'Russian' },
  { key: 'ar', label: 'AR', name: 'Arabic' },
  { key: 'it', label: 'IT', name: 'Italian' },
  { key: 'id', label: 'ID', name: 'Indonesian' },
  { key: 'pt', label: 'PT', name: 'Portuguese' },
  { key: 'ko', label: 'KO', name: 'Korean' },
  { key: 'tr', label: 'TR', name: 'Turkish' }
];

const ACTION_OPTIONS = [
  { key: 'dismiss', label: 'Cerrar popup' },
  { key: 'navigate:inicio', label: 'Ir a Inicio' },
  { key: 'navigate:perfiles', label: 'Ir a Perfiles' },
  { key: 'navigate:agencias', label: 'Ir a Agencias' },
  { key: 'navigate:cuenta', label: 'Ir a Cuenta' },
  { key: 'founders', label: 'Founders checkout' },
  { key: 'openPopup', label: 'Abrir otro popup' },
  { key: 'openUrl', label: 'Abrir enlace' }
];

const TAB_OPTIONS = [
  { key: 'inicio', label: 'Inicio' },
  { key: 'acciones', label: 'Acciones' },
  { key: 'sonidos', label: 'Sonidos' },
  { key: 'tts', label: 'TTS' },
  { key: 'widgets', label: 'Widgets' },
  { key: 'overlay', label: 'Overlay' },
  { key: 'spotify', label: 'Spotify' },
  { key: 'regalos', label: 'Regalos' },
  { key: 'puntos', label: 'Puntos' },
  { key: 'batallas', label: 'Batallas' },
  { key: 'gaming', label: 'Gaming' },
  { key: 'eventos', label: 'Eventos' },
  { key: 'agencias', label: 'Agencias' },
  { key: 'cuenta', label: 'Cuenta' },
  { key: 'integraciones', label: 'Integraciones' },
  { key: 'almacenamiento', label: 'Almacenamiento' }
];

const BANNER_ACTION_OPTIONS = [
  ...TAB_OPTIONS.map((tab) => ({ key: `navigate:${tab.key}`, label: `Ir a ${tab.label}` })),
  { key: 'openPopup', label: 'Abrir popup' },
  { key: 'openUrl', label: 'Abrir enlace' }
];

// ── Audiencia ──
// Mismo shape que evalúan promos.js (app) y el worker (contador): dentro de
// una dimensión se suma (OR), entre dimensiones se exige todo (AND);
// dimensión vacía o 'any' = sin restricción.
const AUDIENCE_PLAN_OPTIONS = [
  { key: 'free', label: 'Free' },
  { key: 'trial', label: 'Trial' },
  { key: 'premium', label: 'Premium' },
  { key: 'performance', label: 'Performance' }
];
const AUDIENCE_PLAN_LABELS = Object.fromEntries(AUDIENCE_PLAN_OPTIONS.map((o) => [o.key, o.label]));
const AUDIENCE_TIKTOK_OPTIONS = [
  { key: 'any', label: 'Todos' },
  { key: 'linked', label: 'Con TikTok vinculado' },
  { key: 'unlinked', label: 'Sin TikTok vinculado' }
];
const AUDIENCE_FOUNDER_OPTIONS = [
  { key: 'any', label: 'Todos' },
  { key: 'founder', label: 'Solo Founders' },
  { key: 'non-founder', label: 'Sin Founders' }
];
const AUDIENCE_AGENCY_OPTIONS = [
  { key: 'any', label: 'Todos' },
  { key: 'in-agency', label: 'Con agencia' },
  { key: 'no-agency', label: 'Sin agencia' }
];
const EMPTY_AUDIENCE_FORM = {
  audiencePlans: [],
  audienceLanguages: [],
  audienceTiktok: 'any',
  audienceFounder: 'any',
  audienceAgency: 'any',
  audienceRegisteredAfter: '',
  audienceRegisteredBefore: '',
  // Lista blanca: el texto tal cual (una entrada por línea). En el doc van sus
  // huellas (audience.whitelist); el texto se guarda aparte, en
  // popups_private/{id}, que solo lee el ADMIN. `Hashes` = las huellas que
  // ya tenía el doc; `Loaded` = se ha podido leer su texto (o no había).
  audienceWhitelist: '',
  audienceWhitelistHashes: [],
  audienceWhitelistLoaded: true
};

// Cuenta atrás del banner horizontal (banner.wide.countdown): los valores de
// fábrica salen de banner-vistas.js, el mismo archivo que la pinta en la app.
const DEFAULT_WIDE_COUNTDOWN = {
  enabled: false,
  at: null,
  x: BannerViews.COUNTDOWN.x,
  y: BannerViews.COUNTDOWN.y,
  fontSize: BannerViews.COUNTDOWN.fontSize,
  color: BannerViews.COUNTDOWN.color,
  bg: BannerViews.COUNTDOWN.bg,
  onEnd: BannerViews.COUNTDOWN.onEnd
};
// Nombres de las cajas en la vista previa (la app los pone en el idioma de cada usuario).
const CD_LABELS = {
  es: { days: 'Días', hours: 'Horas', minutes: 'Min', seconds: 'Seg' },
  en: { days: 'Days', hours: 'Hours', minutes: 'Min', seconds: 'Sec' }
};
const cdLabels = (lang) => CD_LABELS[lang] || CD_LABELS.en;

// Orden de la vista de cards (claves de applySort).
const CARD_SORT_OPTIONS = [
  { key: 'priority:descending', label: 'Prioridad' },
  { key: 'title:ascending', label: 'Titulo A-Z' },
  { key: 'id:ascending', label: 'ID A-Z' },
  { key: 'expiresAt:ascending', label: 'Expira antes' },
  { key: 'state:ascending', label: 'Activos primero' }
];

const DEFAULT_LAYER = {
  html: '',
  htmlEn: '',
  x: 50,
  y: 50,
  width: 52,
  fontSize: 28,
  color: '#ffffff',
  align: 'center',
  weight: 800
};

const DEFAULT_COMPACT_LAYER = { ...DEFAULT_LAYER, fontSize: 18, width: 62 };
const DEFAULT_MODAL_LAYER = { ...DEFAULT_LAYER, fontSize: 34, width: 58 };

const DEFAULT_CTA_STYLE = {
  x: 50,
  y: 92,
  width: 58,
  height: 46,
  fontSize: 15,
  weight: 900,
  radius: 12,
  background: '#fbbf24',
  color: '#06101f'
};

// ── Banner de Inicio horizontal (banner.wide) ──
// Hero a todo el ancho del nuevo Inicio, diseñado sobre un lienzo de
// 1920×320 (6:1). La app lo pinta escalando todo con el ancho: x/y/width en %
// (caja centrada en x,y), fontSize/height/radius en px del lienzo de 1920.
// `bg` en una capa = el texto va en una pastilla (p. ej. «NOVEDAD»).
// Tiene una segunda vista, la pequeña (banner.wide.small, lienzo de 960×240),
// que la app usa con el banner estrecho. Si no se diseña aquí, la app la saca
// sola de la grande (lib/bannerViews.js = el mismo código que la app).
const DEFAULT_CHIP_BG = '#237BFF';

const DEFAULT_WIDE_LAYER = {
  ...DEFAULT_LAYER,
  x: 28,
  y: 50,
  width: 44,
  fontSize: 40,
  align: 'left',
  weight: 800,
  bg: ''
};

const WIDE_STARTER_LAYERS = [
  { html: 'NOVEDAD', x: 16, y: 24, width: 20, fontSize: 15, align: 'left', weight: 800, color: '#ffffff', bg: DEFAULT_CHIP_BG },
  { html: 'Título del banner', x: 28, y: 45, width: 44, fontSize: 40, align: 'left', weight: 800, color: '#ffffff' },
  { html: 'Subtítulo del banner', x: 28, y: 63, width: 44, fontSize: 20, align: 'left', weight: 600, color: '#dbe7ff' }
];

const DEFAULT_WIDE_CTA_STYLE = {
  x: 12.5,
  y: 82,
  width: 13,
  height: 46,
  fontSize: 17,
  weight: 800,
  radius: 10,
  background: DEFAULT_CHIP_BG,
  color: '#ffffff',
  hidden: false
};

const PROMO_PREVIEW_SURFACES = {
  desktopBanner: { width: 451, height: 650 },
  compactBanner: { width: 900, height: 320 },
  wideBanner: { width: 1920, height: 320 },
  wideBannerSmall: { ...BannerViews.SMALL },
  modal: { width: 830, height: 1306 }
};

// El lienzo de cada imagen que se sube: al subirla se reduce a su medida
// (lib/imageShrink.js). Una foto de 5932 × 3955 px de fondo de un banner eran
// casi 100 MB de memoria en la app de cada persona que lo veía.
const UPLOAD_SURFACES = {
  wideBackgroundEs: PROMO_PREVIEW_SURFACES.wideBanner,
  wideBackgroundEn: PROMO_PREVIEW_SURFACES.wideBanner,
  wideSmallBackgroundEs: PROMO_PREVIEW_SURFACES.wideBannerSmall,
  wideSmallBackgroundEn: PROMO_PREVIEW_SURFACES.wideBannerSmall,
  modalImageEs: PROMO_PREVIEW_SURFACES.modal,
  modalImageEn: PROMO_PREVIEW_SURFACES.modal,
  bannerBackgroundEs: PROMO_PREVIEW_SURFACES.desktopBanner,
  bannerBackgroundEn: PROMO_PREVIEW_SURFACES.desktopBanner,
  bannerCompactBackgroundEs: PROMO_PREVIEW_SURFACES.compactBanner,
  bannerCompactBackgroundEn: PROMO_PREVIEW_SURFACES.compactBanner
};

const LEGACY_POPUP_ASSETS = import.meta.glob('../../../renderer/assets/popups/*', {
  eager: true,
  query: '?url',
  import: 'default'
});

const LEGACY_POPUP_ASSET_URLS = Object.fromEntries(
  Object.entries(LEGACY_POPUP_ASSETS).map(([path, url]) => [path.split('/').pop(), url])
);

const EMPTY_FORM = {
  id: '',
  active: true,
  adminOnly: true,
  hasPopup: true,
  ...EMPTY_AUDIENCE_FORM,
  priority: 0,
  expiresAt: '',
  action: 'dismiss',
  bannerCta: 'Ver',
  bannerCtaEn: 'View',
  bannerCtaTranslations: {},
  modalCta: 'Entendido',
  modalCtaEn: 'Got it',
  modalCtaTranslations: {},
  bannerBackgroundEs: '',
  bannerBackgroundEn: '',
  bannerCompactBackgroundEs: '',
  bannerCompactBackgroundEn: '',
  wideBackgroundEs: '',
  wideBackgroundEn: '',
  modalImageEs: '',
  modalImageEn: '',
  wideLayers: [cloneLayer(DEFAULT_WIDE_LAYER)],
  wideCtaStyle: { ...DEFAULT_WIDE_CTA_STYLE },
  // CSS a medida del banner horizontal (las dos vistas), encerrado en él.
  wideCss: '',
  wideHtmlEs: '',
  wideHtmlEn: '',
  // Vista pequeña del banner horizontal: solo se guarda si se personaliza.
  wideSmallCustom: false,
  wideSmallBackgroundEs: '',
  wideSmallBackgroundEn: '',
  wideSmallLayers: [],
  wideSmallCtaStyle: { ...DEFAULT_WIDE_CTA_STYLE },
  // Cuenta atrás del banner horizontal y, con la vista pequeña a medida, su
  // colocación allí ({ x, y, fontSize } o null = la derivada de la grande).
  wideCountdown: { ...DEFAULT_WIDE_COUNTDOWN },
  wideSmallCountdown: null,
  bannerLayers: [cloneLayer(DEFAULT_LAYER)],
  compactLayers: [cloneLayer(DEFAULT_COMPACT_LAYER)],
  modalLayers: [cloneLayer(DEFAULT_MODAL_LAYER)],
  bannerCtaStyle: { ...DEFAULT_CTA_STYLE, x: 50, y: 92, width: 72, height: 52 },
  compactCtaStyle: { ...DEFAULT_CTA_STYLE, x: 50, y: 90, width: 42, height: 42 },
  modalCtaStyle: { ...DEFAULT_CTA_STYLE, x: 50, y: 92, width: 62, height: 52 }
};

function slugify(value) {
  return typingSlug(value).replace(/-+$/g, '');
}

// Lo que se escribe en el campo ID: minúsculas, sin tildes y con guiones, pero deja
// el guion del final para poder seguir escribiendo («mi-» → «mi-banner»).
function typingSlug(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/g, '')
    .slice(0, 80);
}

function clean(value) {
  return String(value || '').trim();
}

function nextCopyId(baseId, existingIds) {
  const root = String(baseId || 'popup').replace(/-copia(?:-\d+)?$/, '') || 'popup';
  let candidate = slugify(`${root}-copia`);
  for (let n = 2; existingIds.has(candidate) && n < 1000; n += 1) {
    candidate = slugify(`${root}-copia-${n}`);
  }
  return candidate;
}

function cloneLayer(layer = DEFAULT_LAYER, overrides = {}) {
  return {
    ...layer,
    ...overrides,
    translations: {
      ...(layer.translations || {}),
      ...(overrides.translations || {})
    }
  };
}

function firstLayer(layers, fallback = DEFAULT_LAYER) {
  return Array.isArray(layers) && layers.length ? layers[0] : cloneLayer(fallback);
}

function layersHaveText(layers) {
  return (Array.isArray(layers) ? layers : []).some((layer) => (
    clean(layer?.html) || clean(layer?.htmlEn) || Object.values(layer?.translations || {}).some((value) => clean(value))
  ));
}

function importLayerTexts(sourceLayers, targetLayers, fallback) {
  const source = Array.isArray(sourceLayers) ? sourceLayers.filter(Boolean) : [];
  if (!source.length) return targetLayers;
  const target = Array.isArray(targetLayers) ? targetLayers : [];
  return source.map((sourceLayer, index) => {
    const base = target[index] || cloneLayer(fallback, { x: sourceLayer.x, y: sourceLayer.y });
    return {
      ...base,
      html: sourceLayer.html || '',
      htmlEn: sourceLayer.htmlEn || '',
      translations: { ...(sourceLayer.translations || {}) }
    };
  });
}

function tsToDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateInput(value) {
  const d = tsToDate(value);
  return d ? d.toISOString().slice(0, 10) : '';
}

function formatDate(value) {
  const d = tsToDate(value);
  return d ? d.toLocaleDateString() : '-';
}

// Archivar solo es cosa del backoffice: saca el popup de la lista sin
// borrarlo. Archivar lo deja inactivo, que es lo que mira la app, así que
// la app no necesita conocer el campo.
function isArchivedPopup(item) {
  return item?.archived === true;
}

function pair(es, en) {
  const out = {};
  if (clean(es)) out.es = clean(es);
  if (clean(en)) out.en = clean(en);
  return Object.keys(out).length ? out : null;
}

function localizedValue(block, lang, key, fallback = '') {
  return block?.[lang]?.[key] || fallback;
}

function translationsFromI18n(block, key) {
  const out = {};
  for (const lang of TRANSLATION_LANGS) {
    const value = localizedValue(block, lang.key, key, '');
    if (clean(value)) out[lang.key] = value;
  }
  return out;
}

function sanitizeHtml(value) {
  const allowed = new Set(['br', 'strong', 'b', 'em', 'i', 'u', 'span', 'small', 'p', 'div', 'mark', 'sup', 'sub']);
  return String(value || '')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/\son[a-z]+\s*=\s*(['"]).*?\1/gi, '')
    .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '')
    .replace(/\s(?:href|src|xlink:href)\s*=\s*(['"])[\s\S]*?\1/gi, '')
    .replace(/\s(?:href|src|xlink:href)\s*=\s*[^\s>]+/gi, '')
    .replace(/<\/?([a-z0-9-]+)(?:\s[^>]*)?>/gi, (match, tag) => {
      const name = String(tag || '').toLowerCase();
      if (!allowed.has(name)) return '';
      return match.startsWith('</') ? `</${name}>` : `<${name}>`;
    });
}

function layerFromSource(source, fallback = DEFAULT_LAYER) {
  const i18n = source?.i18n || {};
  return {
    ...fallback,
    ...(source || {}),
    html: localizedValue(i18n, 'es', 'html', source?.html || ''),
    htmlEn: localizedValue(i18n, 'en', 'html', ''),
    translations: translationsFromI18n(i18n, 'html')
  };
}

function layersFromSource(source, sources, fallback = DEFAULT_LAYER) {
  const rawLayers = Array.isArray(sources) && sources.length
    ? sources
    : (source ? [source] : []);
  const layers = rawLayers
    .filter(Boolean)
    .map((layer) => layerFromSource(layer, fallback));
  return layers.length ? layers : [cloneLayer(fallback)];
}

function buildLayer(layer) {
  const htmlEs = sanitizeHtml(layer.html);
  const htmlEn = sanitizeHtml(layer.htmlEn);
  const i18n = {};
  for (const [lang, value] of Object.entries(layer.translations || {})) {
    if (lang === 'es' || lang === 'en') continue;
    const html = sanitizeHtml(value);
    if (clean(html)) i18n[lang] = { html };
  }
  if (clean(htmlEs)) i18n.es = { html: htmlEs };
  if (clean(htmlEn)) i18n.en = { html: htmlEn };
  return {
    html: htmlEs,
    i18n,
    x: Number(layer.x || 50),
    y: Number(layer.y || 50),
    width: Number(layer.width || 52),
    fontSize: Number(layer.fontSize || 28),
    color: layer.color || '#ffffff',
    align: layer.align || 'center',
    weight: Number(layer.weight || 800)
  };
}

function buildLayerArray(layers, fallback = DEFAULT_LAYER) {
  return (Array.isArray(layers) && layers.length ? layers : [cloneLayer(fallback)])
    .map(buildLayer)
    .filter(builtLayerHasHtml);
}

function builtLayerHasHtml(layer) {
  return clean(layer.html) || Object.values(layer.i18n || {}).some((entry) => clean(entry?.html));
}

function numberOr(value, fallback) {
  if (value === '' || value === null || value === undefined) return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function stripTags(html) {
  return clean(String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' '));
}

// Texto principal del banner horizontal: la primera capa que no es pastilla
// (la pastilla suele ser «NOVEDAD»); si todas lo son, la primera con texto.
function wideTitleText(layers) {
  const list = (Array.isArray(layers) ? layers : []).filter((layer) => stripTags(layer?.html));
  const main = list.find((layer) => !clean(layer.bg)) || list[0];
  return main ? stripTags(main.html) : '';
}

// Capa del banner horizontal: mismo shape que las demás + `bg` (pastilla).
function buildWideLayer(layer) {
  const base = buildLayer(layer);
  return {
    ...base,
    x: numberOr(layer.x, DEFAULT_WIDE_LAYER.x),
    y: numberOr(layer.y, DEFAULT_WIDE_LAYER.y),
    width: Number(layer.width) || DEFAULT_WIDE_LAYER.width,
    fontSize: Number(layer.fontSize) || DEFAULT_WIDE_LAYER.fontSize,
    align: layer.align || DEFAULT_WIDE_LAYER.align,
    bg: clean(layer.bg)
  };
}

function wideLayersFromSource(wide) {
  const raw = Array.isArray(wide?.overlays) ? wide.overlays.filter(Boolean) : [];
  const layers = raw.map((layer) => ({ ...layerFromSource(layer, DEFAULT_WIDE_LAYER), bg: clean(layer.bg) }));
  return layers.length ? layers : [cloneLayer(DEFAULT_WIDE_LAYER)];
}

function wideCtaStyleFromSource(source) {
  return { ...ctaStyleFromSource(source, DEFAULT_WIDE_CTA_STYLE), hidden: source?.hidden === true };
}

// La cuenta atrás guardada → la del formulario (apagada si no hay o no tiene fecha válida).
function wideCountdownFromSource(wide) {
  const cd = BannerViews.countdownOf(wide);
  return cd ? { ...DEFAULT_WIDE_COUNTDOWN, ...cd, enabled: true } : { ...DEFAULT_WIDE_COUNTDOWN };
}

// La del formulario → banner.wide.countdown (o null si está apagada o sin fecha).
function buildWideCountdown(form) {
  if (!form || !form.enabled || !Number.isFinite(Number(form.at))) return null;
  const cd = BannerViews.countdownOf({ countdown: form });
  if (!cd) return null;
  return { ...cd, x: Number(cd.x.toFixed(2)), y: Number(cd.y.toFixed(2)) };
}

// Colocación de la cuenta atrás en una vista: { x, y, fontSize }.
function countdownPlace(src) {
  const base = DEFAULT_WIDE_COUNTDOWN;
  const n = (v, d) => (Number.isFinite(Number(v)) ? Number(v) : d);
  return {
    x: Number(Math.max(0, Math.min(100, n(src?.x, base.x))).toFixed(2)),
    y: Number(Math.max(0, Math.min(100, n(src?.y, base.y))).toFixed(2)),
    fontSize: Math.max(8, Math.min(200, Math.round(n(src?.fontSize, base.fontSize))))
  };
}

// <input type="datetime-local"> ↔ ms, en la hora de este navegador.
function toLocalInput(ms) {
  if (!Number.isFinite(Number(ms)) || !ms) return '';
  const d = new Date(Number(ms));
  const p2 = (v) => String(v).padStart(2, '0');
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T${p2(d.getHours())}:${p2(d.getMinutes())}`;
}
function fromLocalInput(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}
const BROWSER_ZONE = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) { return ''; }
})();

function buildWideCtaStyle(style) {
  const src = style || DEFAULT_WIDE_CTA_STYLE;
  return {
    ...buildCtaStyle(src, DEFAULT_WIDE_CTA_STYLE),
    // x/y/radius pueden ser 0 a proposito (boton pegado al borde, esquinas rectas).
    x: numberOr(src.x, DEFAULT_WIDE_CTA_STYLE.x),
    y: numberOr(src.y, DEFAULT_WIDE_CTA_STYLE.y),
    radius: numberOr(src.radius, DEFAULT_WIDE_CTA_STYLE.radius),
    hidden: src.hidden === true
  };
}

// banner.wide tal cual lo lee el nuevo Inicio. Sin imagen ni textos no se
// escribe: la app trata el popup como «sin banner horizontal».
function buildWideBanner(formData) {
  const background = pair(formData.wideBackgroundEs, formData.wideBackgroundEn);
  const overlays = (Array.isArray(formData.wideLayers) ? formData.wideLayers : [])
    .map(buildWideLayer)
    .filter(builtLayerHasHtml);
  // HTML a medida: se guarda tal cual se escribió (lo limpia quien lo pinta,
  // la app y este lienzo, con BannerViews.cleanHtml).
  const html = pair(String(formData.wideHtmlEs || '').slice(0, BannerViews.HTML_MAX), String(formData.wideHtmlEn || '').slice(0, BannerViews.HTML_MAX));
  if (!background && !overlays.length && !html) return null;
  const wide = {
    background: background || {},
    overlays,
    ctaStyle: buildWideCtaStyle(formData.wideCtaStyle)
  };
  if (clean(formData.wideCss)) wide.css = String(formData.wideCss).slice(0, BannerViews.CSS_MAX);
  if (html) wide.html = html;
  const countdown = buildWideCountdown(formData.wideCountdown);
  if (countdown) wide.countdown = countdown;
  // Vista pequeña diseñada a mano. Sin ella no se escribe `small` y la app
  // la deriva de la grande.
  if (formData.wideSmallCustom) {
    const smallBackground = pair(formData.wideSmallBackgroundEs, formData.wideSmallBackgroundEn);
    wide.small = {
      ...(smallBackground ? { background: smallBackground } : {}),
      overlays: (Array.isArray(formData.wideSmallLayers) ? formData.wideSmallLayers : [])
        .map(buildWideLayer)
        .filter(builtLayerHasHtml),
      ctaStyle: buildWideCtaStyle(formData.wideSmallCtaStyle)
    };
    // Su colocación propia de la cuenta atrás; sin ella, la app la deriva.
    if (countdown && formData.wideSmallCountdown) wide.small.countdown = countdownPlace(formData.wideSmallCountdown);
  }
  return wide;
}

// Vista pequeña automática con lo que hay ahora en el formulario: la que
// pintará la app mientras no se personalice (y el punto de partida al hacerlo).
function derivedSmallForm(formData) {
  const derived = BannerViews.deriveSmall({
    overlays: (Array.isArray(formData.wideLayers) ? formData.wideLayers : []).map(buildWideLayer).filter(builtLayerHasHtml),
    ctaStyle: buildWideCtaStyle(formData.wideCtaStyle),
    countdown: buildWideCountdown(formData.wideCountdown)
  });
  return {
    layers: derived.overlays.map((layer) => ({ ...layerFromSource(layer, DEFAULT_WIDE_LAYER), bg: clean(layer.bg) })),
    ctaStyle: wideCtaStyleFromSource(derived.ctaStyle),
    countdown: derived.countdown
  };
}

// banner.wide.html es { es, en }; un texto suelto vale para todos los idiomas.
function bannerHtmlOf(wide, lang) {
  const html = wide?.html;
  if (typeof html === 'string') return lang === 'es' ? html : '';
  return html && typeof html === 'object' && typeof html[lang] === 'string' ? html[lang] : '';
}

// El HTML a medida que toca en el idioma de la vista previa (como la app:
// español → ES; el resto → EN y, si no hay, ES).
function bannerHtmlFor(formData, lang) {
  return lang === 'es' ? (formData.wideHtmlEs || formData.wideHtmlEn || '') : (formData.wideHtmlEn || formData.wideHtmlEs || '');
}

function popupHasWideBanner(item) {
  const wide = item?.banner?.wide;
  if (!wide || typeof wide !== 'object') return false;
  if (clean(wide.background?.es) || clean(wide.background?.en)) return true;
  if (clean(bannerHtmlOf(wide, 'es')) || clean(bannerHtmlOf(wide, 'en'))) return true;
  return (Array.isArray(wide.overlays) ? wide.overlays : []).some((layer) => (
    layer && (clean(layer.html) || Object.values(layer.i18n || {}).some((entry) => clean(entry?.html)))
  ));
}

// Hoja del lienzo del banner horizontal: las reglas que en la app estan en
// home-dashboard.css (.hm-slide-bg / -layer / -chip / -cta), con la misma
// especificidad (:where no suma), y detras el CSS a medida ya limpio y
// encerrado en este lienzo. --hm-u = 1 px del lienzo, como en la app.
function bannerCanvasSheet(canvasId, css) {
  const root = `:where([data-bn-canvas="${canvasId}"])`;
  return [
    `${root} .bn-bg { position: absolute; inset: 0; background-position: center; background-size: cover; background-repeat: no-repeat; }`,
    `${root} .bn-html { position: absolute; inset: 0; overflow: hidden; font-size: calc(16 * var(--hm-u)); line-height: 1.2; color: #fff; pointer-events: none; }`,
    `${root} .bn-html img { max-width: 100%; }`,
    `${root} .bn-text { transform: translate(-50%, -50%); padding: calc(8 * var(--hm-u)) calc(12 * var(--hm-u)); font-size: calc(var(--fs) * var(--hm-u)); line-height: 1.05; text-shadow: 0 3px 14px rgba(0, 0, 0, 0.75); overflow-wrap: anywhere; }`,
    `${root} .bn-text p { margin: 0; }`,
    `${root} .bn-chip { display: inline-block; padding: 0.32em 0.85em; border-radius: 999px; }`,
    `${root} .bn-button { transform: translate(-50%, -50%); display: grid; place-items: center; min-height: calc(var(--h) * var(--hm-u)); padding: calc(10 * var(--hm-u)) calc(14 * var(--hm-u)); border-radius: calc(var(--r) * var(--hm-u)); font-size: calc(var(--fs) * var(--hm-u)); line-height: 1.1; box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35); }`,
    // Cuenta atrás: los mismos estilos que pone la app (banner-vistas.js).
    BannerViews.countdownCss(`[data-bn-canvas="${canvasId}"]`),
    BannerViews.scopedCss(css, `[data-bn-canvas="${canvasId}"]`)
  ].filter(Boolean).join('\n');
}

function chipStyle(bg) {
  return { display: 'inline-block', background: bg, padding: '.32em .85em', borderRadius: 999 };
}

// <input type="color"> solo acepta #rrggbb (en minusculas); con rgba() u otro
// formato se muestra el color por defecto y el valor real va en el campo CSS.
function hexColorOr(value, fallback) {
  return (/^#[0-9a-f]{6}$/i.test(clean(value)) ? clean(value) : fallback).toLowerCase();
}

function ctaStyleFromSource(source, fallback = DEFAULT_CTA_STYLE) {
  const src = source || {};
  return {
    ...fallback,
    ...src,
    x: Number(src.x ?? fallback.x),
    y: Number(src.y ?? fallback.y),
    width: Number(src.width ?? fallback.width),
    height: Number(src.height ?? fallback.height),
    fontSize: Number(src.fontSize ?? fallback.fontSize),
    weight: Number(src.weight ?? fallback.weight),
    radius: Number(src.radius ?? fallback.radius),
    background: src.background || fallback.background,
    color: src.color || fallback.color
  };
}

function buildCtaStyle(style, fallback = DEFAULT_CTA_STYLE) {
  return {
    x: Number(style.x || fallback.x),
    y: Number(style.y || fallback.y),
    width: Number(style.width || fallback.width),
    height: Number(style.height || fallback.height),
    fontSize: Number(style.fontSize || fallback.fontSize),
    weight: Number(style.weight || fallback.weight),
    radius: Number(style.radius || fallback.radius),
    background: style.background || fallback.background,
    color: style.color || fallback.color
  };
}

function buildFieldI18n(baseTranslations, key, es, en) {
  const out = {};
  for (const [lang, value] of Object.entries(baseTranslations || {})) {
    if (lang === 'es' || lang === 'en') continue;
    if (clean(value)) out[lang] = { [key]: clean(value) };
  }
  if (clean(es)) out.es = { [key]: clean(es) };
  if (clean(en)) out.en = { [key]: clean(en) };
  return out;
}

function audienceFormFromItem(audience) {
  const src = audience && typeof audience === 'object' ? audience : {};
  const tristate = (value, options) => (
    options.some((option) => option.key === value) ? value : 'any'
  );
  return {
    audiencePlans: (Array.isArray(src.plans) ? src.plans : [])
      .filter((plan) => AUDIENCE_PLAN_OPTIONS.some((option) => option.key === plan)),
    audienceLanguages: (Array.isArray(src.languages) ? src.languages : [])
      .filter((lang) => TRANSLATION_LANGS.some((option) => option.key === lang)),
    audienceTiktok: tristate(src.tiktok, AUDIENCE_TIKTOK_OPTIONS),
    audienceFounder: tristate(src.founder, AUDIENCE_FOUNDER_OPTIONS),
    audienceAgency: tristate(src.agency, AUDIENCE_AGENCY_OPTIONS),
    audienceRegisteredAfter: /^\d{4}-\d{2}-\d{2}$/.test(src.registeredAfter || '') ? src.registeredAfter : '',
    audienceRegisteredBefore: /^\d{4}-\d{2}-\d{2}$/.test(src.registeredBefore || '') ? src.registeredBefore : '',
    audienceWhitelist: '',
    audienceWhitelistHashes: PromoWhitelist.cleanHashes(src.whitelist),
    audienceWhitelistLoaded: !PromoWhitelist.cleanHashes(src.whitelist).length
  };
}

// Solo guarda dimensiones que restringen; sin ninguna, audience = null y el
// popup llega a todos (docs antiguos sin el campo se comportan igual).
function buildAudiencePayload(formData) {
  const out = {};
  if (formData.audiencePlans?.length) out.plans = [...formData.audiencePlans];
  if (formData.audienceLanguages?.length) out.languages = [...formData.audienceLanguages];
  if (formData.audienceTiktok && formData.audienceTiktok !== 'any') out.tiktok = formData.audienceTiktok;
  if (formData.audienceFounder && formData.audienceFounder !== 'any') out.founder = formData.audienceFounder;
  if (formData.audienceAgency && formData.audienceAgency !== 'any') out.agency = formData.audienceAgency;
  if (formData.audienceRegisteredAfter) out.registeredAfter = formData.audienceRegisteredAfter;
  if (formData.audienceRegisteredBefore) out.registeredBefore = formData.audienceRegisteredBefore;
  return Object.keys(out).length ? out : null;
}

function audienceSummary(audience) {
  if (!audience || typeof audience !== 'object') return '';
  const parts = [];
  if (Array.isArray(audience.plans) && audience.plans.length) {
    parts.push(audience.plans.map((plan) => AUDIENCE_PLAN_LABELS[plan] || plan).join('+'));
  }
  if (Array.isArray(audience.languages) && audience.languages.length) {
    parts.push(audience.languages.map((lang) => String(lang).toUpperCase()).join(','));
  }
  if (audience.tiktok === 'linked') parts.push('TikTok vinculado');
  if (audience.tiktok === 'unlinked') parts.push('Sin TikTok');
  if (audience.founder === 'founder') parts.push('Founders');
  if (audience.founder === 'non-founder') parts.push('Sin Founders');
  if (audience.agency === 'in-agency') parts.push('Con agencia');
  if (audience.agency === 'no-agency') parts.push('Sin agencia');
  if (audience.registeredAfter) parts.push(`Alta desde ${audience.registeredAfter}`);
  if (audience.registeredBefore) parts.push(`Alta hasta ${audience.registeredBefore}`);
  const wl = PromoWhitelist.cleanHashes(audience.whitelist).length;
  if (wl) parts.push(`Lista blanca: ${wl} ${wl === 1 ? 'entrada' : 'entradas'}`);
  return parts.join(' · ');
}

function toForm(item) {
  const banner = item.banner || {};
  const modal = item.modal || {};
  const bannerAssets = banner.assets || {};
  const modalAssets = modal.assets || {};
  const bannerI18n = banner.i18n || {};
  const modalI18n = modal.i18n || {};
  // Popups sin banner.wide abren el lienzo horizontal vacio (sin textos de
  // ejemplo) para que el admin lo rellene.
  const wide = banner.wide && typeof banner.wide === 'object' ? banner.wide : null;
  const small = BannerViews.hasCustomSmall(wide) ? wide.small : null;
  return {
    ...EMPTY_FORM,
    id: item.id || '',
    active: item.active !== false,
    // Con lista blanca el doc va también como «solo admins» para las versiones
    // anteriores (adminOnlyByWhitelist): eso no es lo que eligió el admin.
    adminOnly: (item.adminOnly === true || item.visibility === 'admins') && item.adminOnlyByWhitelist !== true,
    hasPopup: item.hasPopup !== false && item.bannerOnly !== true && item.modal !== null && item.modal !== false,
    ...audienceFormFromItem(item.audience),
    priority: Number(item.priority || 0),
    expiresAt: dateInput(item.expiresAt),
    action: item.onPrimaryCta || item.action || modal.onPrimaryCta || 'dismiss',
    bannerCta: localizedValue(bannerI18n, 'es', 'cta', banner.cta || 'Ver'),
    bannerCtaEn: localizedValue(bannerI18n, 'en', 'cta', ''),
    bannerCtaTranslations: translationsFromI18n(bannerI18n, 'cta'),
    modalCta: localizedValue(modalI18n, 'es', 'ctaPrimary', modal.ctaPrimary || banner.cta || 'Entendido'),
    modalCtaEn: localizedValue(modalI18n, 'en', 'ctaPrimary', ''),
    modalCtaTranslations: translationsFromI18n(modalI18n, 'ctaPrimary'),
    bannerBackgroundEs: bannerAssets.background?.es || '',
    bannerBackgroundEn: bannerAssets.background?.en || '',
    bannerCompactBackgroundEs: bannerAssets.compactBackground?.es || '',
    bannerCompactBackgroundEn: bannerAssets.compactBackground?.en || '',
    wideBackgroundEs: wide?.background?.es || '',
    wideBackgroundEn: wide?.background?.en || '',
    modalImageEs: modalAssets.image?.es || '',
    modalImageEn: modalAssets.image?.en || '',
    wideLayers: wideLayersFromSource(wide),
    wideCtaStyle: wideCtaStyleFromSource(wide?.ctaStyle),
    wideCss: typeof wide?.css === 'string' ? wide.css : '',
    wideHtmlEs: bannerHtmlOf(wide, 'es'),
    wideHtmlEn: bannerHtmlOf(wide, 'en'),
    wideSmallCustom: !!small,
    wideSmallBackgroundEs: small?.background?.es || '',
    wideSmallBackgroundEn: small?.background?.en || '',
    wideSmallLayers: small ? wideLayersFromSource(small) : [],
    wideSmallCtaStyle: wideCtaStyleFromSource(small?.ctaStyle),
    wideCountdown: wideCountdownFromSource(wide),
    wideSmallCountdown: small && small.countdown && typeof small.countdown === 'object' ? countdownPlace(small.countdown) : null,
    bannerLayers: layersFromSource(banner.overlay, banner.overlays, DEFAULT_LAYER),
    compactLayers: layersFromSource(banner.compactOverlay, banner.compactOverlays, DEFAULT_COMPACT_LAYER),
    modalLayers: layersFromSource(modal.overlay, modal.overlays, DEFAULT_MODAL_LAYER),
    bannerCtaStyle: ctaStyleFromSource(banner.ctaStyle, EMPTY_FORM.bannerCtaStyle),
    compactCtaStyle: ctaStyleFromSource(banner.compactCtaStyle, EMPTY_FORM.compactCtaStyle),
    modalCtaStyle: ctaStyleFromSource(modal.ctaStyle, EMPTY_FORM.modalCtaStyle)
  };
}

function buildPayload(formData) {
  const bannerBackground = pair(formData.bannerBackgroundEs, formData.bannerBackgroundEn);
  const bannerCompactBackground = pair(formData.bannerCompactBackgroundEs, formData.bannerCompactBackgroundEn);
  const modalImage = pair(formData.modalImageEs, formData.modalImageEn);
  const hasPopup = formData.hasPopup !== false;
  const bannerI18n = buildFieldI18n(formData.bannerCtaTranslations, 'cta', clean(formData.bannerCta) || 'Ver', formData.bannerCtaEn);
  const modalI18n = buildFieldI18n(formData.modalCtaTranslations, 'ctaPrimary', clean(formData.modalCta) || 'Entendido', formData.modalCtaEn);
  const bannerLayers = buildLayerArray(formData.bannerLayers, DEFAULT_LAYER);
  const compactLayers = buildLayerArray(formData.compactLayers, DEFAULT_COMPACT_LAYER);
  const modalLayers = buildLayerArray(formData.modalLayers, DEFAULT_MODAL_LAYER);
  const primaryBannerLayer = bannerLayers[0] || buildLayer(firstLayer(formData.bannerLayers, DEFAULT_LAYER));
  const primaryCompactLayer = compactLayers[0] || buildLayer(firstLayer(formData.compactLayers, DEFAULT_COMPACT_LAYER));
  const primaryModalLayer = modalLayers[0] || buildLayer(firstLayer(formData.modalLayers, DEFAULT_MODAL_LAYER));
  const wideBanner = buildWideBanner(formData);
  // El titulo sigue saliendo del banner anterior (la app lo usa para
  // clasificar promos); solo si ese esta vacio se toma del horizontal.
  const title = (
    clean(primaryBannerLayer.html).replace(/<[^>]*>/g, '') ||
    wideTitleText(wideBanner?.overlays)
  ).slice(0, 120);

  return {
    template: 'visual',
    active: !!formData.active,
    adminOnly: !!formData.adminOnly,
    hasPopup,
    bannerOnly: !hasPopup,
    visibility: formData.adminOnly ? 'admins' : 'public',
    priority: Number(formData.priority || 0),
    audience: buildAudiencePayload(formData),
    expiresAt: formData.expiresAt ? new Date(formData.expiresAt) : null,
    action: formData.action || 'dismiss',
    onPrimaryCta: formData.action || 'dismiss',
    banner: {
      template: 'visual',
      layout: 'visual-image',
      title,
      cta: clean(formData.bannerCta) || 'Ver',
      i18n: bannerI18n,
      assets: {
        ...(bannerBackground ? { background: bannerBackground } : {}),
        ...(bannerCompactBackground ? { compactBackground: bannerCompactBackground } : {})
      },
      overlay: primaryBannerLayer,
      overlays: bannerLayers,
      compactOverlay: primaryCompactLayer,
      compactOverlays: compactLayers,
      ctaStyle: buildCtaStyle(formData.bannerCtaStyle),
      compactCtaStyle: buildCtaStyle(formData.compactCtaStyle),
      // Banner horizontal del nuevo Inicio. Los campos de arriba (formato
      // anterior) se siguen escribiendo para las versiones de la app sin el.
      ...(wideBanner ? { wide: wideBanner } : {})
    },
    modal: hasPopup ? {
      template: 'visual',
      layout: 'image',
      ctaPrimary: clean(formData.modalCta) || 'Entendido',
      i18n: modalI18n,
      assets: modalImage ? { image: modalImage } : {},
      overlay: primaryModalLayer,
      overlays: modalLayers,
      ctaStyle: buildCtaStyle(formData.modalCtaStyle),
      onPrimaryCta: formData.action || 'dismiss'
    } : null,
    updatedAt: serverTimestamp()
  };
}

function getPreviewHtml(layer, lang) {
  if (lang === 'es') return sanitizeHtml(layer.html);
  if (lang === 'en') return sanitizeHtml(layer.htmlEn || layer.html);
  return sanitizeHtml(layer.translations?.[lang] || layer.html);
}

function getCtaText(es, en, lang, translations = {}) {
  if (lang === 'es') return clean(es || en);
  if (lang === 'en') return clean(en || es);
  return clean(translations[lang] || es || en);
}

function selectedActionTab(action) {
  const raw = clean(action);
  if (!raw.startsWith('navigate:')) return 'inicio';
  const tab = raw.slice('navigate:'.length);
  return TAB_OPTIONS.some((option) => option.key === tab) ? tab : 'inicio';
}

function isOpenUrlAction(action) {
  return clean(action).startsWith('openUrl:');
}

function isOpenPopupAction(action) {
  return clean(action).startsWith('openPopup:');
}

function actionUrl(action) {
  const raw = clean(action);
  return raw.startsWith('openUrl:') ? raw.slice('openUrl:'.length).trim() : '';
}

function actionPopupId(action) {
  const raw = clean(action);
  return raw.startsWith('openPopup:') ? raw.slice('openPopup:'.length).trim() : '';
}

function normalizeUrlAction(url) {
  const value = clean(url);
  return value ? `openUrl:${value}` : 'openUrl:';
}

function normalizePopupAction(popupId) {
  const value = clean(popupId);
  return value ? `openPopup:${value}` : 'openPopup:';
}

function selectedActionKey(action, options, fallback) {
  if (isOpenUrlAction(action)) return 'openUrl';
  if (isOpenPopupAction(action)) return 'openPopup';
  return options.some((option) => option.key === action) ? action : fallback;
}

function resolvePreviewImageUrl(value) {
  const url = clean(value);
  if (!url) return '';
  const legacyPopupMatch = url.match(/(?:^|\/)assets\/popups\/([^?#]+)/i);
  if (legacyPopupMatch) {
    const localAssetUrl = LEGACY_POPUP_ASSET_URLS[legacyPopupMatch[1]];
    if (localAssetUrl) return localAssetUrl;
  }
  if (/^https?:\/\//i.test(url) || url.startsWith('blob:') || url.startsWith('data:')) return url;
  if (url.startsWith('assets/')) return `https://storage.tikcontrol.live/${url}`;
  if (url.startsWith('/assets/')) return `https://storage.tikcontrol.live${url}`;
  if (url.startsWith('public/')) return `https://storage.tikcontrol.live/${url}`;
  if (url.startsWith('/public/')) return `https://storage.tikcontrol.live${url}`;
  return url;
}

// `snap`: paso (en %) al arrastrar. En el lienzo horizontal 1 % de ancho son
// 19 px, asi que ahi se usa un paso mas fino.
// `onSelect` (editor compacto): el lienzo se ve tal cual lo pinta la app — sin
// cajas alrededor de los textos — y al pulsar un texto o el boton se elige
// (`selected`: indice del texto | 'cta') para editarlo en el panel de al lado.
function VisualCanvas({ title, surface = PROMO_PREVIEW_SURFACES.desktopBanner, image, layers, lang, cta, ctaStyle, onLayersChange, onCtaStyleChange, hint, fallbackLayer = DEFAULT_LAYER, snap = 1, selected = null, onSelect = null, bare = false, hooks = null, css = '', html = '', countdown = null, onCountdownChange = null }) {
  const ref = useRef(null);
  const dragRef = useRef(null);
  // La cuenta atrás de la vista previa avanza sola, como en la app.
  const [nowMs, setNowMs] = useState(() => Date.now());
  const countdownAt = countdown && countdown.at ? Number(countdown.at) : 0;
  useEffect(() => {
    if (!countdownAt) return undefined;
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [countdownAt]);
  const [imageOk, setImageOk] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [previewScale, setPreviewScale] = useState(1);
  const safeLayers = Array.isArray(layers) && layers.length ? layers : [cloneLayer(fallbackLayer)];
  const imageUrl = resolvePreviewImageUrl(image);
  const ratio = `${surface.width} / ${surface.height}`;
  const quiet = typeof onSelect === 'function';
  // `hooks` ('wide' | 'small') = lienzo del banner horizontal: lleva las
  // clases bn-* y reparte los estilos igual que la app (en linea solo lo que
  // la app pone en linea; lo demas, en una hoja) para que el CSS a medida
  // pise aqui exactamente lo mismo que pisara alli.
  const canvasId = useId();
  const hooked = hooks === 'wide' || hooks === 'small';
  const hookSheet = hooked ? bannerCanvasSheet(canvasId, css) : '';
  const customHtml = hooked ? BannerViews.cleanHtml(html) : '';
  // La vista (.bn-wide / .bn-small) va DENTRO del lienzo, igual que en la app
  // va dentro del slide: en @scope los selectores cuelgan de :scope, y
  // «.bn-small .bn-chip» no casaria con la clase puesta en la propia raiz.
  const View = hooked ? 'div' : Fragment;
  const viewProps = hooked ? { className: `bn-${hooks}`, style: { position: 'absolute', inset: 0 } } : {};

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    function updateScale() {
      const rect = node.getBoundingClientRect();
      const widthScale = rect.width / surface.width;
      const heightScale = rect.height / surface.height;
      const nextScale = Math.min(widthScale || 1, heightScale || 1);
      setPreviewScale(Number.isFinite(nextScale) && nextScale > 0 ? nextScale : 1);
    }

    updateScale();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateScale) : null;
    observer?.observe(node);
    window.addEventListener('resize', updateScale);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateScale);
    };
  }, [surface.height, surface.width]);

  useEffect(() => {
    setImageOk(false);
    setImageFailed(false);
    if (!imageUrl) return undefined;
    const img = new Image();
    img.onload = () => {
      setImageOk(true);
      setImageFailed(false);
    };
    img.onerror = () => {
      setImageOk(false);
      setImageFailed(true);
    };
    img.src = imageUrl;
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl]);

  // Posicion del puntero en % del lienzo (sin redondear).
  function pointFromEvent(event) {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: ((event.clientX - rect.left) / rect.width) * 100,
      y: ((event.clientY - rect.top) / rect.height) * 100
    };
  }

  // El elemento se arrastra desde donde se agarra (no salta a centrarse en el
  // puntero) y un simple clic solo lo elige: no lo mueve.
  function startDrag(event, target) {
    event.preventDefault();
    event.stopPropagation();
    const point = pointFromEvent(event);
    const current = target === 'cta' ? ctaStyle : target === 'cd' ? countdown : safeLayers[target];
    if (!point || !current) return;
    dragRef.current = {
      target,
      dx: point.x - Number(current.x || 0),
      dy: point.y - Number(current.y || 0),
      startX: event.clientX,
      startY: event.clientY,
      moved: false
    };
    onSelect?.(target);
  }

  function moveDrag(event) {
    const drag = dragRef.current;
    if (!drag) return;
    if (!drag.moved && Math.abs(event.clientX - drag.startX) + Math.abs(event.clientY - drag.startY) < 4) return;
    drag.moved = true;
    const point = pointFromEvent(event);
    if (!point) return;
    const step = Number(snap) > 0 ? Number(snap) : 1;
    const place = (value) => Number((Math.round(Math.max(0, Math.min(100, value)) / step) * step).toFixed(2));
    const x = place(point.x - drag.dx);
    const y = place(point.y - drag.dy);
    if (drag.target === 'cta') {
      if (onCtaStyleChange && ctaStyle) onCtaStyleChange({ ...ctaStyle, x, y });
      return;
    }
    if (drag.target === 'cd') {
      if (onCountdownChange && countdown) onCountdownChange({ ...countdown, x, y });
      return;
    }
    onLayersChange(safeLayers.map((layer, layerIndex) => (
      layerIndex === drag.target ? { ...layer, x, y } : layer
    )));
  }

  function endDrag() {
    dragRef.current = null;
  }

  const frame = (isSelected) => (quiet
    ? `outline outline-1 outline-offset-2 ${isSelected ? 'outline-primary-400' : 'outline-transparent hover:outline-white/50'}`
    : 'border border-white/35 bg-black/15');

  const canvas = (
    <div
      ref={ref}
      className="relative overflow-hidden rounded-lg border border-default-200 bg-black"
      {...(hooked ? { 'data-bn-canvas': canvasId } : {})}
      style={{
        aspectRatio: ratio,
        ...(hooked ? { '--hm-u': `${previewScale}px` } : {
          backgroundImage: imageOk ? `url("${imageUrl.replace(/"/g, '\\"')}")` : 'linear-gradient(135deg, #111827, #25324a)',
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        })
      }}
      onPointerDown={() => onSelect?.(null)}
      onPointerMove={moveDrag}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
    >
      {hooked && <style>{hookSheet}</style>}
      <View {...viewProps}>
      {hooked && (
        <div
          className="bn-bg"
          style={{ backgroundImage: imageOk ? `url("${imageUrl.replace(/"/g, '\\"')}")` : 'linear-gradient(135deg, #111827, #25324a)' }}
        />
      )}
      {/* HTML a medida: la misma capa que pinta la app, ya limpia. */}
      {hooked && customHtml && <div className="bn-html" dangerouslySetInnerHTML={{ __html: customHtml }} />}
      {!imageUrl && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-xs text-slate-400">
          {quiet ? 'Sin imagen de fondo: súbela desde «Fondo»' : 'Sube una imagen para previsualizar'}
        </div>
      )}
      {imageFailed && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center px-6 text-center text-xs leading-relaxed text-warning-200">
          No se pudo cargar la imagen. Si es una ruta assets/popups, vuelve a subirla desde Backoffice para guardarla en R2.
        </div>
      )}
      {!imageFailed && safeLayers.map((layer, index) => {
        const text = getPreviewHtml(layer, lang);
        // En el editor compacto un texto vacio se ve como hueco para elegirlo.
        const html = { __html: text || (quiet ? `<span style="opacity:.55;font-weight:500">Texto ${index + 1}</span>` : `Texto HTML ${index + 1}`) };
        // Con `bg` el contenido va dentro de una pastilla inline, como en la app.
        const chipBg = clean(layer.bg);
        return (
          <div
            key={index}
            className={`absolute cursor-move rounded-md ${frame(selected === index)} ${hooked ? `bn-text bn-text-${index + 1}` : ''}`}
            style={{
              left: `${layer.x}%`,
              top: `${layer.y}%`,
              width: `${layer.width}%`,
              color: layer.color,
              fontWeight: layer.weight,
              textAlign: layer.align,
              ...(hooked ? { '--fs': Number(layer.fontSize || 0) } : {
                fontSize: `${Math.max(7, Number(layer.fontSize || 0) * previewScale)}px`,
                transform: 'translate(-50%, -50%)',
                lineHeight: 1.05,
                padding: `${Math.max(3, 8 * previewScale)}px ${Math.max(5, 12 * previewScale)}px`,
                textShadow: '0 3px 14px rgba(0,0,0,.75)'
              }),
              ...(quiet && !text && selected !== index ? { outlineStyle: 'dashed', outlineColor: 'rgba(255,255,255,.4)' } : {})
            }}
            onPointerDown={(event) => startDrag(event, index)}
            {...(chipBg ? {} : { dangerouslySetInnerHTML: html })}
          >
            {chipBg ? (
              <span
                className={hooked ? 'bn-chip' : undefined}
                style={hooked ? { background: chipBg } : chipStyle(chipBg)}
                dangerouslySetInnerHTML={html}
              />
            ) : null}
          </div>
        );
      })}
      {!imageFailed && hooked && countdownAt ? (
        <div
          className={`bn-countdown cursor-move select-none ${quiet ? frame(selected === 'cd') : ''}`}
          role="timer"
          style={{
            left: `${countdown.x}%`,
            top: `${countdown.y}%`,
            color: countdown.color,
            pointerEvents: 'auto',
            '--fs': Number(countdown.fontSize || 0),
            '--cd-bg': countdown.bg || 'transparent'
          }}
          onPointerDown={(event) => startDrag(event, 'cd')}
          dangerouslySetInnerHTML={{ __html: BannerViews.countdownCellsHtml(BannerViews.countdownParts(countdownAt, nowMs), cdLabels(lang)) }}
        />
      ) : null}
      {!imageFailed && cta && ctaStyle && !ctaStyle.hidden && (
        <div
          className={`absolute cursor-move select-none text-center ${hooked ? 'bn-button' : 'shadow-lg'} ${quiet ? frame(selected === 'cta') : ''}`}
          style={{
            left: `${ctaStyle.x}%`,
            top: `${ctaStyle.y}%`,
            width: `${ctaStyle.width}%`,
            background: ctaStyle.background,
            color: ctaStyle.color,
            fontWeight: ctaStyle.weight,
            ...(hooked ? {
              '--h': Number(ctaStyle.height || 46),
              '--fs': Number(ctaStyle.fontSize || 0),
              '--r': Number(ctaStyle.radius || 0)
            } : {
              minHeight: `${Math.max(24, Number(ctaStyle.height || 46) * previewScale)}px`,
              transform: 'translate(-50%, -50%)',
              display: 'grid',
              placeItems: 'center',
              borderRadius: `${Math.max(2, Number(ctaStyle.radius || 0) * previewScale)}px`,
              fontSize: `${Math.max(7, Number(ctaStyle.fontSize || 0) * previewScale)}px`,
              padding: `${Math.max(4, 10 * previewScale)}px ${Math.max(8, 14 * previewScale)}px`
            })
          }}
          onPointerDown={(event) => startDrag(event, 'cta')}
        >
          {cta}
        </div>
      )}
      </View>
    </div>
  );

  if (bare) return canvas;
  return (
    <div className="rounded-xl border border-default-200 bg-default-50 p-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold">{title}</div>
          {hint && <div className="text-xs text-default-500">{hint}</div>}
        </div>
        <Chip size="sm" variant="flat">{lang.toUpperCase()}</Chip>
      </div>
      {canvas}
    </div>
  );
}

// `withChip`: muestra el control de pastilla (`bg`) de cada capa; solo lo
// entiende el banner horizontal del nuevo Inicio.
function LayersControls({ label, layers, fallback, onChange, importLabel, onImport, importDisabled, withChip = false, hint }) {
  const [collapsed, setCollapsed] = useState(false);
  const safeLayers = Array.isArray(layers) && layers.length ? layers : [cloneLayer(fallback)];
  // Una capa nueva o vaciada no hereda la pastilla de la anterior.
  const chipReset = withChip ? { bg: '' } : {};

  function updateLayer(index, patch) {
    onChange(safeLayers.map((layer, layerIndex) => (
      layerIndex === index ? { ...layer, ...patch } : layer
    )));
  }

  function addLayer() {
    const base = safeLayers[safeLayers.length - 1] || fallback;
    onChange([
      ...safeLayers,
      cloneLayer(base, {
        html: '',
        htmlEn: '',
        translations: {},
        ...chipReset,
        x: Math.min(90, Number(base.x || fallback.x || 50) + 6),
        y: Math.min(90, Number(base.y || fallback.y || 50) + 6)
      })
    ]);
  }

  function removeLayer(index) {
    if (safeLayers.length <= 1) {
      onChange([cloneLayer(safeLayers[0] || fallback, { html: '', htmlEn: '', translations: {}, ...chipReset })]);
      return;
    }
    onChange(safeLayers.filter((_, layerIndex) => layerIndex !== index));
  }

  return (
    <div className="rounded-xl border border-default-200 p-4">
      <div className={`flex flex-wrap items-center justify-between gap-3 ${collapsed ? '' : 'mb-3'}`}>
        <button
          type="button"
          className="flex items-center gap-2 text-left"
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((value) => !value)}
        >
          <span className={`text-xs text-default-400 transition-transform ${collapsed ? '' : 'rotate-90'}`} aria-hidden="true">▸</span>
          <span className="text-sm font-semibold">{label}</span>
          <span className="rounded-full bg-default-100 px-2 text-xs text-default-500">{safeLayers.length}</span>
        </button>
        <div className="flex flex-wrap items-center gap-2">
          {onImport && (
            <Button size="sm" variant="flat" isDisabled={importDisabled} onPress={onImport}>
              {importLabel}
            </Button>
          )}
          <Button size="sm" variant="flat" color="primary" onPress={addLayer}>
            Agregar texto
          </Button>
        </div>
      </div>
      <div className={collapsed ? 'hidden' : 'flex flex-col gap-4'}>
        {hint && <p className="text-xs text-default-500">{hint}</p>}
        {safeLayers.map((layer, index) => (
          <div key={index} className="rounded-lg border border-default-200 p-3">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="text-xs font-semibold text-default-500">Texto {index + 1}</div>
              <Button
                size="sm"
                variant="light"
                color="danger"
                onPress={() => removeLayer(index)}
              >
                Eliminar
              </Button>
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
              <Textarea
                label="HTML ES"
                value={layer.html}
                onValueChange={(html) => updateLayer(index, { html })}
                minRows={3}
              />
              <Textarea
                label="HTML EN"
                value={layer.htmlEn}
                onValueChange={(htmlEn) => updateLayer(index, { htmlEn })}
                minRows={3}
              />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-6">
              <Input label="X %" type="number" value={String(layer.x)} onValueChange={(x) => updateLayer(index, { x })} />
              <Input label="Y %" type="number" value={String(layer.y)} onValueChange={(y) => updateLayer(index, { y })} />
              <Input label="Ancho %" type="number" value={String(layer.width)} onValueChange={(width) => updateLayer(index, { width })} />
              <Input label="Tamano" type="number" value={String(layer.fontSize)} onValueChange={(fontSize) => updateLayer(index, { fontSize })} />
              <Input label="Peso" type="number" value={String(layer.weight)} onValueChange={(weight) => updateLayer(index, { weight })} />
              <Input label="Color" type="color" value={layer.color} onValueChange={(color) => updateLayer(index, { color })} />
            </div>
            <Select
              className="mt-3 max-w-xs"
              label="Alineacion"
              selectedKeys={[layer.align]}
              onSelectionChange={(keys) => updateLayer(index, { align: String(Array.from(keys)[0] || 'center') })}
            >
              <SelectItem key="left">Izquierda</SelectItem>
              <SelectItem key="center">Centro</SelectItem>
              <SelectItem key="right">Derecha</SelectItem>
            </Select>
            {withChip && (
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Input
                  className="max-w-[120px]"
                  label="Pastilla"
                  type="color"
                  isDisabled={!clean(layer.bg)}
                  value={hexColorOr(layer.bg, DEFAULT_CHIP_BG)}
                  onValueChange={(bg) => updateLayer(index, { bg })}
                />
                <Input
                  className="max-w-[220px]"
                  label="Fondo pastilla (CSS)"
                  placeholder="Sin fondo"
                  value={layer.bg || ''}
                  onValueChange={(bg) => updateLayer(index, { bg })}
                />
                <Checkbox
                  isSelected={!clean(layer.bg)}
                  onValueChange={(noBg) => updateLayer(index, { bg: noBg ? '' : DEFAULT_CHIP_BG })}
                >
                  <span className="text-sm">Sin fondo</span>
                </Checkbox>
                <span className="text-xs text-default-400">Con color, el texto se pinta como pastilla (p. ej. «NOVEDAD»).</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// `allowHidden`: añade «Sin botón» (style.hidden); solo el banner horizontal lo usa.
function CtaStyleControls({ label, style, onChange, allowHidden = false, hint }) {
  return (
    <div className="rounded-xl border border-default-200 p-4">
      {allowHidden ? (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold">{label}</h3>
            {hint && <p className="text-xs text-default-500">{hint}</p>}
          </div>
          <Checkbox isSelected={!!style.hidden} onValueChange={(hidden) => onChange({ ...style, hidden })}>
            <span className="text-sm">Sin botón</span>
          </Checkbox>
        </div>
      ) : (
        <h3 className="mb-3 text-sm font-semibold">{label}</h3>
      )}
      <div className={`grid grid-cols-2 gap-3 md:grid-cols-4 ${allowHidden && style.hidden ? 'opacity-50' : ''}`}>
        <Input label="X %" type="number" value={String(style.x)} onValueChange={(x) => onChange({ ...style, x })} />
        <Input label="Y %" type="number" value={String(style.y)} onValueChange={(y) => onChange({ ...style, y })} />
        <Input label="Ancho %" type="number" value={String(style.width)} onValueChange={(width) => onChange({ ...style, width })} />
        <Input label="Alto px" type="number" value={String(style.height)} onValueChange={(height) => onChange({ ...style, height })} />
        <Input label="Tamano" type="number" value={String(style.fontSize)} onValueChange={(fontSize) => onChange({ ...style, fontSize })} />
        <Input label="Peso" type="number" value={String(style.weight)} onValueChange={(weight) => onChange({ ...style, weight })} />
        <Input label="Radio" type="number" value={String(style.radius)} onValueChange={(radius) => onChange({ ...style, radius })} />
        <Input label="Fondo" type="color" value={style.background} onValueChange={(background) => onChange({ ...style, background })} />
        <Input label="Texto" type="color" value={style.color} onValueChange={(color) => onChange({ ...style, color })} />
      </div>
    </div>
  );
}

// Firma estable de la audiencia de un doc, para cachear su alcance: mismos
// filtros → misma llamada al contador aunque sean popups distintos.
function audienceSignatureForItem(item) {
  return JSON.stringify(buildAudiencePayload(audienceFormFromItem(item?.audience)));
}

const CARD_THUMB_WIDTH = 120;

// Ids de las promos que antes venian escritas en la app (Founders Edition,
// Afiliados, One-Click). La app nueva ignora estos docs; siguen aqui porque
// las versiones anteriores los usan para mostrar u ocultar SU promo nativa:
// si se borran, esas versiones vuelven a enseñarla a todo el mundo.
const RETIRED_POPUP_IDS = new Set(['founders-edition', 'referral-system', 'sistema-de-referidos', 'profiles-one-click', 'perfiles-one-click']);

// Miniatura de solo lectura del banner grande: misma composicion que
// VisualCanvas (imagen + capas HTML + CTA posicionados en %) pero a escala
// fija, para reconocer cada popup de un vistazo en la lista.
function PromoCardThumb({ item, onClick }) {
  const banner = item.banner || {};
  const assets = banner.assets || {};
  const modalAssets = item.modal?.assets || {};
  const surface = PROMO_PREVIEW_SURFACES.desktopBanner;
  const scale = CARD_THUMB_WIDTH / surface.width;
  const image = resolvePreviewImageUrl(
    assets.background?.es || assets.background?.en || modalAssets.image?.es || modalAssets.image?.en || ''
  );
  const layers = Array.isArray(banner.overlays) && banner.overlays.length
    ? banner.overlays
    : (banner.overlay ? [banner.overlay] : []);
  const cta = localizedValue(banner.i18n, 'es', 'cta', banner.cta || '');
  const ctaStyle = banner.ctaStyle;

  return (
    <div
      className={`relative shrink-0 self-start overflow-hidden rounded-lg border border-default-200 bg-black ${onClick ? 'cursor-pointer' : ''}`}
      style={{
        width: CARD_THUMB_WIDTH,
        aspectRatio: `${surface.width} / ${surface.height}`,
        // El gradiente queda detras: si la URL falla o no hay imagen, se ve el
        // como placeholder sin necesidad de estado de error.
        backgroundImage: `${image ? `url("${image.replace(/"/g, '\\"')}"), ` : ''}linear-gradient(135deg, #111827, #25324a)`,
        backgroundPosition: 'center',
        backgroundSize: 'cover'
      }}
      onClick={onClick}
      title={onClick ? 'Editar popup' : undefined}
    >
      {layers.map((layer, index) => (
        <div
          key={index}
          className="absolute"
          style={{
            left: `${Number(layer.x ?? 50)}%`,
            top: `${Number(layer.y ?? 50)}%`,
            width: `${Number(layer.width ?? 52)}%`,
            color: layer.color || '#ffffff',
            fontSize: `${Math.max(4, Number(layer.fontSize || 28) * scale)}px`,
            fontWeight: Number(layer.weight || 800),
            textAlign: layer.align || 'center',
            transform: 'translate(-50%, -50%)',
            lineHeight: 1.05,
            textShadow: '0 1px 4px rgba(0,0,0,.75)'
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(layer.i18n?.es?.html || layer.html || '') }}
        />
      ))}
      {cta && ctaStyle ? (
        <div
          className="absolute grid select-none place-items-center overflow-hidden"
          style={{
            left: `${Number(ctaStyle.x ?? 50)}%`,
            top: `${Number(ctaStyle.y ?? 92)}%`,
            width: `${Number(ctaStyle.width ?? 58)}%`,
            minHeight: `${Math.max(8, Number(ctaStyle.height || 46) * scale)}px`,
            transform: 'translate(-50%, -50%)',
            borderRadius: `${Math.max(1, Number(ctaStyle.radius || 12) * scale)}px`,
            background: ctaStyle.background || '#fbbf24',
            color: ctaStyle.color || '#06101f',
            fontSize: `${Math.max(4, Number(ctaStyle.fontSize || 15) * scale)}px`,
            fontWeight: Number(ctaStyle.weight || 900)
          }}
        >
          {cta}
        </div>
      ) : null}
    </div>
  );
}

// Miniatura del banner horizontal (banner.wide) a todo el ancho de la card.
// La escala sale del ancho real con unidades de contenedor (cqw): 1 px del
// lienzo de 1920 = 100cqw / 1920, igual que hace la app con su ancho.
function WideCardThumb({ item, onClick }) {
  const banner = item.banner || {};
  const wide = banner.wide || {};
  const surface = PROMO_PREVIEW_SURFACES.wideBanner;
  const image = resolvePreviewImageUrl(wide.background?.es || wide.background?.en || '');
  const layers = Array.isArray(wide.overlays) ? wide.overlays.filter(Boolean) : [];
  const cta = localizedValue(banner.i18n, 'es', 'cta', banner.cta || '');
  const ctaStyle = wide.ctaStyle;
  const countdown = BannerViews.countdownOf(wide);
  const scaled = (value, fallback, min) => (
    `max(${min}px, calc(${Number(value) || fallback} * 100cqw / ${surface.width}))`
  );

  return (
    <div
      className={`relative w-full overflow-hidden rounded-lg border border-default-200 bg-black ${onClick ? 'cursor-pointer' : ''}`}
      data-bn-thumb=""
      style={{
        aspectRatio: `${surface.width} / ${surface.height}`,
        containerType: 'inline-size',
        '--hm-u': `calc(100cqw / ${surface.width})`,
        backgroundImage: `${image ? `url("${image.replace(/"/g, '\\"')}"), ` : ''}linear-gradient(135deg, #111827, #25324a)`,
        backgroundPosition: 'center',
        backgroundSize: 'cover'
      }}
      onClick={onClick}
      title={onClick ? 'Editar popup' : undefined}
    >
      {layers.map((layer, index) => {
        const html = { __html: sanitizeHtml(layer.i18n?.es?.html || layer.html || '') };
        const chipBg = clean(layer.bg);
        return (
          <div
            key={index}
            className="absolute"
            style={{
              left: `${numberOr(layer.x, DEFAULT_WIDE_LAYER.x)}%`,
              top: `${numberOr(layer.y, DEFAULT_WIDE_LAYER.y)}%`,
              width: `${Number(layer.width) || DEFAULT_WIDE_LAYER.width}%`,
              color: layer.color || '#ffffff',
              fontSize: scaled(layer.fontSize, DEFAULT_WIDE_LAYER.fontSize, 4),
              fontWeight: Number(layer.weight || 800),
              textAlign: layer.align || 'left',
              transform: 'translate(-50%, -50%)',
              lineHeight: 1.05,
              textShadow: '0 1px 4px rgba(0,0,0,.75)'
            }}
            {...(chipBg ? {} : { dangerouslySetInnerHTML: html })}
          >
            {chipBg ? <span style={chipStyle(chipBg)} dangerouslySetInnerHTML={html} /> : null}
          </div>
        );
      })}
      {cta && ctaStyle && !ctaStyle.hidden ? (
        <div
          className="absolute grid select-none place-items-center overflow-hidden"
          style={{
            left: `${numberOr(ctaStyle.x, DEFAULT_WIDE_CTA_STYLE.x)}%`,
            top: `${numberOr(ctaStyle.y, DEFAULT_WIDE_CTA_STYLE.y)}%`,
            width: `${Number(ctaStyle.width) || DEFAULT_WIDE_CTA_STYLE.width}%`,
            minHeight: scaled(ctaStyle.height, DEFAULT_WIDE_CTA_STYLE.height, 8),
            transform: 'translate(-50%, -50%)',
            borderRadius: scaled(numberOr(ctaStyle.radius, DEFAULT_WIDE_CTA_STYLE.radius), 0, 0),
            background: ctaStyle.background || DEFAULT_WIDE_CTA_STYLE.background,
            color: ctaStyle.color || DEFAULT_WIDE_CTA_STYLE.color,
            fontSize: scaled(ctaStyle.fontSize, DEFAULT_WIDE_CTA_STYLE.fontSize, 4),
            fontWeight: Number(ctaStyle.weight || DEFAULT_WIDE_CTA_STYLE.weight)
          }}
        >
          {cta}
        </div>
      ) : null}
      {countdown ? (
        <>
          <style>{BannerViews.countdownCss('[data-bn-thumb]')}</style>
          <div
            className="bn-countdown"
            style={{ left: `${countdown.x}%`, top: `${countdown.y}%`, color: countdown.color, '--fs': countdown.fontSize, '--cd-bg': countdown.bg || 'transparent' }}
            dangerouslySetInnerHTML={{ __html: BannerViews.countdownCellsHtml(BannerViews.countdownParts(countdown.at, Date.now()), cdLabels('es')) }}
          />
        </>
      ) : null}
    </div>
  );
}

const STATS_RANGE_OPTIONS = [
  { key: 'all', label: 'Total' },
  { key: 'last30', label: '30 dias' },
  { key: 'last7', label: '7 dias' }
];

function formatCount(value) {
  return Number(value || 0).toLocaleString('es-ES');
}

function formatAvg(value) {
  const n = Number(value || 0);
  return n.toLocaleString('es-ES', { maximumFractionDigits: 1 });
}

function ctrPercent(clicks, impressions) {
  const i = Number(impressions || 0);
  if (!i) return '0%';
  const pct = (Number(clicks || 0) / i) * 100;
  return `${pct.toLocaleString('es-ES', { maximumFractionDigits: pct < 10 ? 1 : 0 })}%`;
}

// Impresiones y clicks del popup (GET /api/backoffice/popups/stats, tablas de
// la migración 015). La media por usuario y los usuarios alcanzados solo
// existen para el total acumulado: por día no se guarda el desglose por uid.
function PopupStatsBlock({ stats, range, statsState }) {
  if (statsState === 'unavailable') {
    return (
      <div className="rounded-lg border border-dashed border-default-200 px-3 py-2 text-xs text-default-400">
        Estadisticas no disponibles (falta desplegar el Worker o aplicar la migracion 015).
      </div>
    );
  }
  if (!stats) {
    return (
      <div className="rounded-lg border border-dashed border-default-200 px-3 py-2 text-xs text-default-400">
        {statsState === 'loading' ? 'Cargando estadisticas…' : 'Sin impresiones registradas todavia.'}
      </div>
    );
  }
  const scoped = range === 'all' ? stats : (stats[range] || {});
  const rows = [
    {
      key: 'banner',
      label: 'Banner',
      impressions: scoped.bannerImpressions,
      clicks: scoped.bannerClicks,
      users: stats.bannerUsers,
      avg: stats.avgBannerPerUser
    },
    {
      key: 'modal',
      label: 'Popup',
      impressions: scoped.modalImpressions,
      clicks: scoped.modalClicks,
      users: stats.modalUsers,
      avg: stats.avgModalPerUser
    }
  ];
  return (
    <div className="rounded-lg bg-content2 px-3 py-2">
      <div className="grid grid-cols-[auto_1fr_1fr_1fr] gap-x-3 gap-y-1 text-xs">
        <div className="text-[10px] uppercase tracking-wider text-default-400" />
        <div className="text-[10px] uppercase tracking-wider text-default-400">Impresiones</div>
        <div className="text-[10px] uppercase tracking-wider text-default-400">Clicks</div>
        <div className="text-[10px] uppercase tracking-wider text-default-400">CTR</div>
        {rows.map((row) => (
          <div key={row.key} className="contents">
            <div className="font-medium text-default-600">{row.label}</div>
            <div
              className="text-default-700"
              title={range === 'all'
                ? `${formatCount(row.users)} usuarios · ${formatAvg(row.avg)} veces de media por usuario`
                : 'Media por usuario solo disponible en Total'}
            >
              <span className="font-semibold text-foreground">{formatCount(row.impressions)}</span>
              {range === 'all' && row.users > 0 ? (
                <span className="text-default-400"> · {formatAvg(row.avg)}/usuario</span>
              ) : null}
            </div>
            <div className="font-semibold text-foreground">{formatCount(row.clicks)}</div>
            <div className="text-default-600">{ctrPercent(row.clicks, row.impressions)}</div>
          </div>
        ))}
      </div>
      {range === 'all' && (
        <div className="mt-1.5 text-[11px] text-default-400">
          {formatCount(stats.users)} usuarios alcanzados
          {stats.lastSeenAt ? ` · ultima actividad ${formatDate(stats.lastSeenAt)}` : ''}
        </div>
      )}
    </div>
  );
}

function PopupCard({ item, reach, stats, statsState, statsRange, canWrite, onEdit, onDuplicate, onDelete, onToggleActive, onArchive, onUnarchive }) {
  const whitelistCount = PromoWhitelist.cleanHashes(item.audience?.whitelist).length;
  const byWhitelist = whitelistCount > 0 && item.adminOnlyByWhitelist === true;
  const isAdminsOnly = (item.adminOnly || item.visibility === 'admins') && !byWhitelist;
  const countdown = BannerViews.countdownOf(item.banner?.wide);
  const isActive = item.active !== false;
  const isArchived = isArchivedPopup(item);
  const summary = audienceSummary(item.audience);
  const hasExpiry = !!tsToDate(item.expiresAt);
  // Con banner horizontal se muestra ese arriba a todo el ancho; sin el, la
  // miniatura vertical de antes y un aviso para rehacerlo.
  const hasWide = popupHasWideBanner(item);
  const isRetired = RETIRED_POPUP_IDS.has(item.id);

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-default-200 bg-content1 p-4">
      {hasWide && <WideCardThumb item={item} onClick={canWrite ? onEdit : undefined} />}
      <div className="flex flex-1 gap-4">
        {!hasWide && <PromoCardThumb item={item} onClick={canWrite ? onEdit : undefined} />}
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="min-w-0">
            <div className="truncate font-medium">{item.banner?.title || item.id}</div>
            <div className="truncate text-xs text-default-400">{item.id}</div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {isRetired ? (
              <Chip
                size="sm"
                variant="solid"
                color="default"
                title="Promo que antes venia dentro de la app. La app nueva la ignora; mantenla INACTIVA y no la borres hasta que no queden versiones anteriores: si se borra, esas versiones vuelven a enseñarla."
              >
                Solo apps anteriores · no borrar
              </Chip>
            ) : !hasWide && (
              <Chip size="sm" variant="solid" color="warning" title="Este popup aun no tiene el banner horizontal del nuevo Inicio">
                Sin banner horizontal
              </Chip>
            )}
            {byWhitelist ? (
              <Chip size="sm" variant="flat" color="primary" title="Solo los usuarios de la lista blanca (y el equipo de TikControl). En las versiones anteriores de la app, solo el equipo.">
                Lista blanca · {whitelistCount}
              </Chip>
            ) : (
              <Chip size="sm" variant="flat" color={isAdminsOnly ? 'warning' : 'success'}>
                {isAdminsOnly ? 'Solo admins' : 'Publico'}
              </Chip>
            )}
            {countdown && (
              <Chip size="sm" variant="flat" color="secondary" title={countdown.onEnd === 'hide' ? 'Al llegar a cero deja de salir' : 'Al llegar a cero se queda en 00'}>
                Cuenta atrás hasta {new Date(countdown.at).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </Chip>
            )}
            <Chip size="sm" variant="flat">Prioridad {Number(item.priority || 0)}</Chip>
            {hasExpiry && <Chip size="sm" variant="flat" color="secondary">Expira {formatDate(item.expiresAt)}</Chip>}
          </div>
          <div className="text-xs text-default-500">
            <span className="font-medium text-default-600">Audiencia: </span>
            {summary || 'Todos los usuarios'}
            {/* Con lista blanca, el alcance de la tarjeta no la tendría en cuenta: no se enseña. */}
            {reach?.ok && !whitelistCount ? (
              <span className="text-default-400">
                {' '}· ≈{Number(reach.matching || 0).toLocaleString('es-ES')} usuarios
              </span>
            ) : null}
          </div>
          <PopupStatsBlock stats={stats} range={statsRange} statsState={statsState} />
          <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-1">
            {isArchived ? (
              <Chip size="sm" variant="flat" title="No sale en la app. Para volver a usarlo, desarchívalo y actívalo.">
                Archivado{tsToDate(item.archivedAt) ? ` el ${formatDate(item.archivedAt)}` : ''}
              </Chip>
            ) : canWrite ? (
              <Switch size="sm" isSelected={isActive} onValueChange={onToggleActive}>
                <span className="text-xs">{isActive ? 'Activo' : 'Inactivo'}</span>
              </Switch>
            ) : (
              <Chip size="sm" variant="flat" color={isActive ? 'success' : 'default'}>
                {isActive ? 'Activo' : 'Inactivo'}
              </Chip>
            )}
            {canWrite ? (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="flat" onPress={onEdit}>Editar</Button>
                <Button size="sm" variant="flat" onPress={onDuplicate}>Duplicar</Button>
                {isArchived ? (
                  <Button size="sm" variant="flat" onPress={onUnarchive}>Desarchivar</Button>
                ) : (
                  <Button size="sm" variant="flat" onPress={onArchive} title="Lo quita de la lista sin borrarlo. Lo tienes en «Ver archivados».">
                    Archivar
                  </Button>
                )}
                <Button size="sm" variant="flat" color="danger" onPress={onDelete}>Eliminar</Button>
              </div>
            ) : (
              <span className="text-xs text-default-400">solo lectura</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AudienceTriSelect({ label, value, options, onChange }) {
  return (
    <Select
      label={label}
      disallowEmptySelection
      selectedKeys={[value || 'any']}
      onSelectionChange={(keys) => onChange(String(Array.from(keys)[0] || 'any'))}
    >
      {options.map((option) => <SelectItem key={option.key}>{option.label}</SelectItem>)}
    </Select>
  );
}

function AssetField({ label, value, onChange, onUpload, uploading }) {
  const inputRef = useRef(null);
  return (
    <div className="flex gap-2">
      <Input label={label} value={value} onValueChange={onChange} />
      <Button
        className="mt-6"
        variant="flat"
        isLoading={uploading}
        onPress={() => inputRef.current?.click()}
      >
        Subir
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onUpload(file);
        }}
      />
    </div>
  );
}

function previewImage(previewUrls, formData, esField, enField, lang) {
  return lang === 'en'
    ? (previewUrls[enField] || formData[enField] || previewUrls[esField] || formData[esField])
    : (previewUrls[esField] || formData[esField] || previewUrls[enField] || formData[enField]);
}

// ── Editor compacto de un lienzo (banner horizontal o popup) ──
// El lienzo siempre a la vista; debajo (banner) o al lado (popup) una barra
// con «Fondo», una ficha por texto, «+ Texto» y «Botón», y UN panel con los
// ajustes de lo que este elegido (tambien se elige pulsandolo en el lienzo).
const MINI_INPUT = 'h-8 w-full rounded-md border border-default-200 bg-default-100 px-2 text-sm text-foreground outline-none transition-colors focus:border-primary';
const WEIGHT_OPTIONS = [[400, 'Normal'], [500, 'Media'], [600, 'Seminegrita'], [700, 'Negrita'], [800, 'Extra negrita'], [900, 'Black']];
const ALIGN_OPTIONS = [['left', 'Izquierda', 'M4 6h16M4 12h10M4 18h13'], ['center', 'Centro', 'M4 6h16M7 12h10M5.5 18h13'], ['right', 'Derecha', 'M4 6h16M10 12h10M7 18h13']];

function MiniField({ label, className = '', children }) {
  return (
    <label className={`flex min-w-0 flex-col gap-1 ${className}`}>
      <span className="truncate text-[11px] font-medium leading-none text-default-500">{label}</span>
      {children}
    </label>
  );
}

function MiniNumber({ label, value, onChange, step = 1, className = 'w-[68px]' }) {
  return (
    <MiniField label={label} className={className}>
      <input className={MINI_INPUT} type="number" step={step} value={String(value ?? '')} onChange={(event) => onChange(event.target.value)} />
    </MiniField>
  );
}

function MiniColor({ label, value, onChange, disabled = false }) {
  return (
    <MiniField label={label} className="w-[52px]">
      <input
        className={`h-8 w-full cursor-pointer rounded-md border border-default-200 bg-default-100 p-0.5 ${disabled ? 'opacity-40' : ''}`}
        type="color"
        disabled={disabled}
        value={hexColorOr(value, '#ffffff')}
        onChange={(event) => onChange(event.target.value)}
      />
    </MiniField>
  );
}

// Texto corto que crece si hace falta (admite <b>, <br>…).
function MiniText({ label, value, onChange, placeholder, className = 'min-w-[170px] flex-1' }) {
  return (
    <MiniField label={label} className={className}>
      <textarea
        className={`${MINI_INPUT} min-h-8 resize-y py-1.5 leading-tight`}
        rows={1}
        placeholder={placeholder}
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
      />
    </MiniField>
  );
}

function MiniWeight({ value, onChange }) {
  const current = Number(value) || 800;
  return (
    <MiniField label="Grosor" className="w-[124px]">
      <select className={MINI_INPUT} value={String(current)} onChange={(event) => onChange(Number(event.target.value))}>
        {!WEIGHT_OPTIONS.some(([w]) => w === current) && <option value={current}>{current}</option>}
        {WEIGHT_OPTIONS.map(([w, name]) => <option key={w} value={w}>{name}</option>)}
      </select>
    </MiniField>
  );
}

function MiniAlign({ value, onChange }) {
  return (
    <MiniField label="Alineación">
      <div className="flex h-8 overflow-hidden rounded-md border border-default-200">
        {ALIGN_OPTIONS.map(([key, name, path]) => (
          <button
            key={key}
            type="button"
            title={name}
            aria-label={name}
            aria-pressed={value === key}
            className={`grid w-8 place-items-center transition-colors ${value === key ? 'bg-primary text-white' : 'bg-default-100 text-default-600 hover:bg-default-200'}`}
            onClick={() => onChange(key)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d={path} /></svg>
          </button>
        ))}
      </div>
    </MiniField>
  );
}

// Una imagen del fondo: miniatura, «Subir», la URL y quitar.
function MiniAsset({ label, value, preview, ratio, onChange, onUpload, uploading }) {
  const inputRef = useRef(null);
  const url = resolvePreviewImageUrl(preview || value);
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="w-6 shrink-0 text-xs font-semibold text-default-500">{label}</span>
      <span
        className="h-8 shrink-0 rounded border border-default-200 bg-default-100 bg-cover bg-center"
        style={{ aspectRatio: ratio, maxWidth: 96, backgroundImage: url ? `url("${url.replace(/"/g, '\\"')}")` : undefined }}
      />
      <Button size="sm" variant="flat" color="primary" isLoading={uploading} onPress={() => inputRef.current?.click()}>
        Subir
      </Button>
      <input
        className={`${MINI_INPUT} min-w-0 flex-1`}
        placeholder="…o pega la URL de la imagen"
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
      />
      {clean(value) && (
        <button type="button" className="shrink-0 px-1 text-xs text-default-500 hover:text-danger" onClick={() => onChange('')}>
          Quitar
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onUpload(file);
        }}
      />
    </div>
  );
}

function ToolChip({ active, onClick, children, title, tone = 'default' }) {
  const idle = tone === 'primary'
    ? 'border-primary-300 text-primary hover:bg-primary-50'
    : 'border-default-200 bg-default-100 text-default-700 hover:bg-default-200';
  return (
    <button
      type="button"
      title={title}
      aria-pressed={active}
      className={`inline-flex h-8 max-w-[220px] items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${active ? 'border-primary bg-primary text-white' : idle}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * cfg: {
 *   title, hint, surface, orientation: 'wide' | 'tall', snap, fallbackLayer,
 *   layersKey, ctaStyleKey, ctaKey, ctaEnKey, ctaTranslationsKey,
 *   bgEsKey, bgEnKey, bgHint, uploadKind,
 *   bgFallbackEsKey, bgFallbackEnKey (imagen que se enseña si la propia está vacía),
 *   withChip (pastilla en los textos), allowHidden («Sin botón»)
 * }
 * header / footer: contenido extra arriba y abajo de la tarjeta (selector de vista…).
 */
function CanvasEditor({ cfg, formData, setFormData, previewUrls, lang, uploadingField, onImageChange, onUpload, header = null, footer = null }) {
  const [selected, setSelected] = useState(null);   // null | 'bg' | 'cta' | indice del texto
  const layers = Array.isArray(formData[cfg.layersKey]) && formData[cfg.layersKey].length
    ? formData[cfg.layersKey]
    : [cloneLayer(cfg.fallbackLayer)];
  const ctaStyle = formData[cfg.ctaStyleKey];
  const ctaText = getCtaText(formData[cfg.ctaKey], formData[cfg.ctaEnKey], lang, formData[cfg.ctaTranslationsKey]);
  const image = previewImage(previewUrls, formData, cfg.bgEsKey, cfg.bgEnKey, lang)
    || (cfg.bgFallbackEsKey ? previewImage(previewUrls, formData, cfg.bgFallbackEsKey, cfg.bgFallbackEnKey, lang) : '');
  const tall = cfg.orientation === 'tall';
  const ratio = `${cfg.surface.width} / ${cfg.surface.height}`;
  const layer = typeof selected === 'number' ? layers[selected] : null;

  const setLayers = (next) => setFormData((f) => ({ ...f, [cfg.layersKey]: next }));
  const setCta = (patch) => setFormData((f) => ({ ...f, [cfg.ctaStyleKey]: { ...f[cfg.ctaStyleKey], ...patch } }));
  const patchLayer = (patch) => setLayers(layers.map((item, index) => (index === selected ? { ...item, ...patch } : item)));
  const chipReset = cfg.withChip ? { bg: '' } : {};

  function addLayer() {
    const base = layers[layers.length - 1] || cfg.fallbackLayer;
    setLayers([...layers, cloneLayer(base, {
      html: '', htmlEn: '', translations: {}, ...chipReset,
      x: Math.min(90, Number(base.x || 50) + 4),
      y: Math.min(90, Number(base.y || 50) + 12)
    })]);
    setSelected(layers.length);
  }

  function removeLayer() {
    if (layers.length <= 1) setLayers([cloneLayer(layers[0] || cfg.fallbackLayer, { html: '', htmlEn: '', translations: {}, ...chipReset })]);
    else setLayers(layers.filter((_, index) => index !== selected));
    setSelected(null);
  }

  const layerName = (item, index) => stripTags(getPreviewHtml(item, lang)) || `Texto ${index + 1}`;

  // Cuenta atrás: la fecha, los colores y qué pasa al terminar son de la vista
  // grande (wideCountdown); la pequeña a medida guarda solo su colocación.
  const cdBase = cfg.countdownKey ? (formData[cfg.countdownKey] || DEFAULT_WIDE_COUNTDOWN) : null;
  const cdOn = !!(cdBase && cdBase.enabled && cdBase.at);
  const cdPlace = cfg.countdownPlaceKey
    ? (formData[cfg.countdownPlaceKey] || derivedSmallForm(formData).countdown || countdownPlace(cdBase))
    : cdBase;
  const cdShown = cdOn ? { ...cdBase, ...(cfg.countdownPlaceKey ? countdownPlace(cdPlace) : {}) } : null;
  const setCountdown = (patch) => setFormData((f) => (cfg.countdownPlaceKey
    ? { ...f, [cfg.countdownPlaceKey]: countdownPlace({ ...(f[cfg.countdownPlaceKey] || cdPlace), ...patch }) }
    : { ...f, [cfg.countdownKey]: { ...(f[cfg.countdownKey] || DEFAULT_WIDE_COUNTDOWN), ...patch } }));
  const cdWhen = cdOn ? new Date(Number(cdBase.at)).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  const cdBoxHex = /^#[0-9a-f]{6}/i.test(clean(cdBase?.bg)) ? clean(cdBase.bg).slice(0, 7) : '#000000';

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <ToolChip active={selected === 'bg'} onClick={() => setSelected(selected === 'bg' ? null : 'bg')} title="Imagen de fondo">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></svg>
        Fondo
      </ToolChip>
      <span className="h-5 w-px bg-default-200" aria-hidden="true" />
      {layers.map((item, index) => (
        <ToolChip key={index} active={selected === index} onClick={() => setSelected(selected === index ? null : index)} title="Editar este texto">
          <span className="font-serif text-[13px] font-bold opacity-70" aria-hidden="true">T</span>
          <span className="truncate">{layerName(item, index)}</span>
        </ToolChip>
      ))}
      <ToolChip tone="primary" onClick={addLayer} title="Añadir un texto">+ Texto</ToolChip>
      {cfg.countdownKey && (!cfg.countdownPlaceKey || cdOn) && (
        <>
          <span className="h-5 w-px bg-default-200" aria-hidden="true" />
          <ToolChip
            active={selected === 'cd'}
            tone={cdOn ? 'default' : 'primary'}
            onClick={() => setSelected(selected === 'cd' ? null : 'cd')}
            title="Cuenta atrás: días, horas, minutos y segundos hasta una fecha"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 2.5M9 2h6" /></svg>
            <span className="truncate">{cdOn ? `Cuenta atrás: ${cdWhen}` : '+ Cuenta atrás'}</span>
          </ToolChip>
        </>
      )}
      <span className="h-5 w-px bg-default-200" aria-hidden="true" />
      <ToolChip active={selected === 'cta'} onClick={() => setSelected(selected === 'cta' ? null : 'cta')} title="Botón">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><rect x="3" y="7" width="18" height="10" rx="3" /><path d="M8 12h8" /></svg>
        <span className="truncate">{cfg.allowHidden && ctaStyle?.hidden ? 'Sin botón' : `Botón: ${ctaText || '—'}`}</span>
      </ToolChip>
    </div>
  );

  let panel = null;
  if (selected === 'cd' && cfg.countdownKey && cfg.countdownPlaceKey) {
    panel = (
      <>
        <div className="flex flex-wrap items-end gap-2">
          <MiniNumber label="Tamaño" value={cdPlace?.fontSize} onChange={(fontSize) => setCountdown({ fontSize })} />
          <MiniNumber label="X %" value={cdPlace?.x} onChange={(x) => setCountdown({ x })} step={0.5} />
          <MiniNumber label="Y %" value={cdPlace?.y} onChange={(y) => setCountdown({ y })} step={0.5} />
        </div>
        <p className="text-xs text-default-500">Aquí solo se coloca. La fecha, los colores y qué pasa al terminar se cambian en la vista grande.</p>
      </>
    );
  } else if (selected === 'cd' && cfg.countdownKey) {
    const off = !cdBase.enabled;
    panel = (
      <>
        <Checkbox size="sm" isSelected={!off} onValueChange={(enabled) => setCountdown({ enabled, ...(enabled && !cdBase.at ? { at: Math.ceil((Date.now() + 7 * 864e5) / 6e4) * 6e4 } : {}) })}>
          <span className="text-xs">Mostrar una cuenta atrás en el banner</span>
        </Checkbox>
        <div className={`flex flex-wrap items-end gap-2 ${off ? 'pointer-events-none opacity-50' : ''}`}>
          <MiniField label={`Termina el (hora de ${BROWSER_ZONE || 'tu navegador'})`} className="w-[210px]">
            <input className={MINI_INPUT} type="datetime-local" value={toLocalInput(cdBase.at)} onChange={(event) => setCountdown({ at: fromLocalInput(event.target.value) })} />
          </MiniField>
          <MiniField label="Al llegar a cero" className="w-[240px]">
            <select className={MINI_INPUT} value={cdBase.onEnd === 'zero' ? 'zero' : 'hide'} onChange={(event) => setCountdown({ onEnd: event.target.value })}>
              <option value="hide">Quitar el banner y su popup</option>
              <option value="zero">Dejarla en 00</option>
            </select>
          </MiniField>
          <MiniNumber label="Tamaño" value={cdBase.fontSize} onChange={(fontSize) => setCountdown({ fontSize })} />
          <MiniColor label="Cifras" value={cdBase.color} onChange={(color) => setCountdown({ color })} />
          <MiniField label="Cajas">
            <div className="flex h-8 items-center gap-1.5">
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer accent-primary"
                title="Cada cifra en una caja de color, algo transparente"
                checked={!!clean(cdBase.bg)}
                onChange={(event) => setCountdown({ bg: event.target.checked ? DEFAULT_WIDE_COUNTDOWN.bg : '' })}
              />
              <input
                type="color"
                className={`h-8 w-10 cursor-pointer rounded-md border border-default-200 bg-default-100 p-0.5 ${clean(cdBase.bg) ? '' : 'opacity-40'}`}
                disabled={!clean(cdBase.bg)}
                value={cdBoxHex}
                onChange={(event) => setCountdown({ bg: `${event.target.value}a6` })}
              />
            </div>
          </MiniField>
          <MiniNumber label="X %" value={cdBase.x} onChange={(x) => setCountdown({ x })} step={0.5} />
          <MiniNumber label="Y %" value={cdBase.y} onChange={(y) => setCountdown({ y })} step={0.5} />
        </div>
        <p className="text-xs text-default-500">
          Cada usuario ve lo que le falta, en su idioma. Las versiones anteriores de la app enseñan el banner sin la cuenta atrás: para que
          también allí deje de salir al terminar, pon ese día en «Expira».
        </p>
      </>
    );
  } else if (selected === 'bg') {
    panel = (
      <>
        <div className={`grid gap-2 ${tall ? '' : 'xl:grid-cols-2'}`}>
          <MiniAsset
            label="ES"
            ratio={ratio}
            value={formData[cfg.bgEsKey]}
            preview={previewUrls[cfg.bgEsKey]}
            uploading={uploadingField === cfg.bgEsKey}
            onChange={(value) => onImageChange(cfg.bgEsKey, value)}
            onUpload={(file) => onUpload(cfg.bgEsKey, cfg.uploadKind, 'es', file)}
          />
          <MiniAsset
            label="EN"
            ratio={ratio}
            value={formData[cfg.bgEnKey]}
            preview={previewUrls[cfg.bgEnKey]}
            uploading={uploadingField === cfg.bgEnKey}
            onChange={(value) => onImageChange(cfg.bgEnKey, value)}
            onUpload={(file) => onUpload(cfg.bgEnKey, cfg.uploadKind, 'en', file)}
          />
        </div>
        <p className="text-xs text-default-500">{cfg.bgHint}</p>
      </>
    );
  } else if (selected === 'cta' && ctaStyle) {
    const off = cfg.allowHidden && !!ctaStyle.hidden;
    panel = (
      <>
        <div className={`flex flex-wrap items-end gap-2 ${off ? 'opacity-50' : ''}`}>
          <MiniText label="Texto ES" value={formData[cfg.ctaKey]} onChange={(value) => setFormData((f) => ({ ...f, [cfg.ctaKey]: value }))} className="min-w-[130px] flex-1" />
          <MiniText label="Texto EN" value={formData[cfg.ctaEnKey]} onChange={(value) => setFormData((f) => ({ ...f, [cfg.ctaEnKey]: value }))} className="min-w-[130px] flex-1" />
          <MiniNumber label="Tamaño" value={ctaStyle.fontSize} onChange={(fontSize) => setCta({ fontSize })} />
          <MiniWeight value={ctaStyle.weight} onChange={(weight) => setCta({ weight })} />
          <MiniColor label="Fondo" value={ctaStyle.background} onChange={(background) => setCta({ background })} />
          <MiniColor label="Texto" value={ctaStyle.color} onChange={(color) => setCta({ color })} />
          <MiniNumber label="Ancho %" value={ctaStyle.width} onChange={(width) => setCta({ width })} step={0.5} />
          <MiniNumber label="Alto" value={ctaStyle.height} onChange={(height) => setCta({ height })} />
          <MiniNumber label="Radio" value={ctaStyle.radius} onChange={(radius) => setCta({ radius })} />
          <MiniNumber label="X %" value={ctaStyle.x} onChange={(x) => setCta({ x })} step={0.5} />
          <MiniNumber label="Y %" value={ctaStyle.y} onChange={(y) => setCta({ y })} step={0.5} />
        </div>
        {cfg.allowHidden && (
          <Checkbox size="sm" isSelected={off} onValueChange={(hidden) => setCta({ hidden })}>
            <span className="text-xs">Sin botón (el banner entero sigue siendo pulsable)</span>
          </Checkbox>
        )}
      </>
    );
  } else if (layer) {
    const hasChip = !!clean(layer.bg);
    panel = (
      <div className="flex flex-wrap items-end gap-2">
        <MiniText label="Texto ES" value={layer.html} onChange={(html) => patchLayer({ html })} placeholder="Escribe el texto…" />
        <MiniText label="Texto EN" value={layer.htmlEn} onChange={(htmlEn) => patchLayer({ htmlEn })} placeholder="Si se deja vacío se usa el ES" />
        <MiniNumber label="Tamaño" value={layer.fontSize} onChange={(fontSize) => patchLayer({ fontSize })} />
        <MiniWeight value={layer.weight} onChange={(weight) => patchLayer({ weight })} />
        <MiniColor label="Color" value={layer.color} onChange={(color) => patchLayer({ color })} />
        <MiniAlign value={layer.align} onChange={(align) => patchLayer({ align })} />
        {cfg.withChip && (
          <MiniField label="Pastilla">
            <div className="flex h-8 items-center gap-1.5">
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer accent-primary"
                title="Pinta el texto dentro de una pastilla de color (p. ej. «NOVEDAD»)"
                checked={hasChip}
                onChange={(event) => patchLayer({ bg: event.target.checked ? DEFAULT_CHIP_BG : '' })}
              />
              <input
                type="color"
                className={`h-8 w-10 cursor-pointer rounded-md border border-default-200 bg-default-100 p-0.5 ${hasChip ? '' : 'opacity-40'}`}
                disabled={!hasChip}
                value={hexColorOr(layer.bg, DEFAULT_CHIP_BG)}
                onChange={(event) => patchLayer({ bg: event.target.value })}
              />
            </div>
          </MiniField>
        )}
        <MiniNumber label="Ancho %" value={layer.width} onChange={(width) => patchLayer({ width })} step={0.5} />
        <MiniNumber label="X %" value={layer.x} onChange={(x) => patchLayer({ x })} step={0.5} />
        <MiniNumber label="Y %" value={layer.y} onChange={(y) => patchLayer({ y })} step={0.5} />
        <Button size="sm" variant="light" color="danger" className="h-8" onPress={removeLayer}>
          Eliminar
        </Button>
      </div>
    );
  }

  const panelTitle = selected === 'bg' ? 'Imagen de fondo'
    : selected === 'cd' ? 'Cuenta atrás'
    : selected === 'cta' ? 'Botón'
      : layer ? `Texto ${selected + 1}` : '';

  const inspector = panel ? (
    <div className="flex flex-col gap-2 rounded-lg border border-primary-200 bg-default-50 p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-primary">{panelTitle}</span>
        <button type="button" className="text-xs text-default-500 hover:text-foreground" onClick={() => setSelected(null)}>
          Cerrar
        </button>
      </div>
      {panel}
    </div>
  ) : (
    <p className="px-1 text-xs text-default-400">
      Pulsa un texto o el botón (aquí o en el lienzo) para editarlo. Arrástralos en el lienzo para colocarlos.
    </p>
  );

  const canvas = (
    <VisualCanvas
      bare
      surface={cfg.surface}
      image={image}
      layers={layers}
      fallbackLayer={cfg.fallbackLayer}
      snap={cfg.snap}
      lang={lang}
      cta={ctaText}
      ctaStyle={ctaStyle}
      hooks={cfg.hooks || null}
      css={cfg.cssKey ? formData[cfg.cssKey] : ''}
      html={cfg.hooks ? bannerHtmlFor(formData, lang) : ''}
      selected={selected}
      onSelect={setSelected}
      onLayersChange={setLayers}
      onCtaStyleChange={(next) => setFormData((f) => ({ ...f, [cfg.ctaStyleKey]: next }))}
      countdown={cdShown}
      onCountdownChange={(next) => setCountdown({ x: next.x, y: next.y })}
    />
  );

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-default-200 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <h3 className="text-sm font-semibold">{cfg.title}</h3>
          {cfg.hint && <span className="text-xs text-default-500">{cfg.hint}</span>}
        </div>
        <Chip size="sm" variant="flat">{lang.toUpperCase()}</Chip>
      </div>
      {header}
      {tall ? (
        <div className="grid items-start gap-3 sm:grid-cols-[minmax(200px,300px)_minmax(0,1fr)]">
          {canvas}
          <div className="flex min-w-0 flex-col gap-3">
            {toolbar}
            {inspector}
          </div>
        </div>
      ) : (
        <>
          {canvas}
          {toolbar}
          {inspector}
        </>
      )}
      {footer}
    </section>
  );
}

const WIDE_EDITOR = {
  title: 'Banner de Inicio',
  hint: 'Horizontal, 1920 × 320 px (6:1).',
  surface: PROMO_PREVIEW_SURFACES.wideBanner,
  orientation: 'wide',
  snap: 0.5,
  fallbackLayer: DEFAULT_WIDE_LAYER,
  layersKey: 'wideLayers',
  ctaStyleKey: 'wideCtaStyle',
  ctaKey: 'bannerCta',
  ctaEnKey: 'bannerCtaEn',
  ctaTranslationsKey: 'bannerCtaTranslations',
  bgEsKey: 'wideBackgroundEs',
  bgEnKey: 'wideBackgroundEn',
  bgHint: 'Imagen de 1920 × 320 px (proporción 6:1). Si no hay imagen EN se usa la ES. Una más grande se reduce sola al subirla.',
  uploadKind: 'banner',
  withChip: true,
  allowHidden: true,
  hooks: 'wide',
  cssKey: 'wideCss',
  countdownKey: 'wideCountdown'
};

// Vista pequeña del banner: mismo editor sobre su lienzo de 960×240. El texto
// del botón es el de la grande; sin imagen propia se ve la de la grande.
const WIDE_SMALL_EDITOR = {
  ...WIDE_EDITOR,
  title: 'Banner de Inicio · vista pequeña',
  hint: `Horizontal, ${BannerViews.SMALL.width} × ${BannerViews.SMALL.height} px (4:1).`,
  surface: PROMO_PREVIEW_SURFACES.wideBannerSmall,
  fallbackLayer: { ...DEFAULT_WIDE_LAYER, x: 43, width: 70, fontSize: 34 },
  layersKey: 'wideSmallLayers',
  ctaStyleKey: 'wideSmallCtaStyle',
  bgEsKey: 'wideSmallBackgroundEs',
  bgEnKey: 'wideSmallBackgroundEn',
  bgFallbackEsKey: 'wideBackgroundEs',
  bgFallbackEnKey: 'wideBackgroundEn',
  hooks: 'small',
  countdownPlaceKey: 'wideSmallCountdown',
  bgHint: `Imagen de ${BannerViews.SMALL.width} × ${BannerViews.SMALL.height} px (proporción 4:1). Si se deja vacía se usa la de la vista grande, recortada por los lados.`
};

const CSS_PROBLEMS = {
  too_long: `Demasiado largo: como mucho ${BannerViews.CSS_MAX} caracteres.`,
  unbalanced: 'Hay una llave { } sin abrir o sin cerrar: así no se aplica nada.'
};

// Para empezar con algo que funciona: una cinta en la esquina, hecha con una
// clase propia, que se adapta a la vista pequeña.
const BANNER_CODE_EXAMPLE = {
  html: '<div class="mi-cinta">\n  <strong>-30 %</strong>\n  <span>solo esta semana</span>\n</div>',
  css: [
    '.mi-cinta {',
    '  position: absolute;',
    '  right: 4%;',
    '  top: 16%;',
    '  display: grid;',
    '  gap: calc(4 * var(--hm-u));',
    '  padding: calc(14 * var(--hm-u)) calc(22 * var(--hm-u));',
    '  border-radius: calc(14 * var(--hm-u));',
    '  background: linear-gradient(135deg, #ff5ea8, #7c3aed);',
    '  text-align: center;',
    '  transform: rotate(4deg);',
    '}',
    '.mi-cinta strong { font-size: calc(44 * var(--hm-u)); line-height: 1; }',
    '.mi-cinta span { font-size: calc(16 * var(--hm-u)); opacity: 0.9; }',
    '/* En la vista pequeña: más recogida y sin meterse bajo la flecha del carrusel */',
    '.bn-small .mi-cinta { top: 10%; right: 10%; transform: none; }'
  ].join('\n')
};

const CODE_BOX = 'min-h-[150px] w-full resize-y rounded-md border border-default-200 bg-default-100 p-2 font-mono text-xs leading-relaxed text-foreground outline-none transition-colors focus:border-primary';

function RefList({ title, rows }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-default-600">{title}</span>
      <dl className="flex flex-col gap-1">
        {rows.map((row) => (
          <div key={row.name}>
            <dt className="inline"><code className="text-primary">{row.name}</code></dt>{' '}
            <dd className="inline text-default-500">{row.what}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// Nombres sueltos en fila, con espacios entre ellos para que partan de línea.
function CodeWords({ words }) {
  return words.map((word, index) => (
    <Fragment key={word}>
      <code>{word}</code>
      {index < words.length - 1 ? ' ' : ''}
    </Fragment>
  ));
}

// HTML y CSS a medida del banner: para lo que el editor no da (formas, una
// cinta, degradados en un texto, animaciones…). Solo afecta a ese banner, en
// sus dos vistas. Debajo, los nombres que se pueden usar: los mismos que
// pintan la app y este lienzo (BannerViews.REFERENCE).
function BannerCodePanel({ formData, setFormData, onClose }) {
  const [htmlLang, setHtmlLang] = useState('es');
  const htmlKey = htmlLang === 'en' ? 'wideHtmlEn' : 'wideHtmlEs';
  const htmlValue = formData[htmlKey] || '';
  const cssProblem = BannerViews.cssProblem(formData.wideCss);
  const htmlTooLong = BannerViews.htmlProblem(htmlValue) === 'too_long';
  const dropped = BannerViews.cleanHtmlReport(htmlValue).dropped;
  const ref = BannerViews.REFERENCE;
  const empty = !clean(formData.wideHtmlEs) && !clean(formData.wideHtmlEn) && !clean(formData.wideCss);

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-primary-200 bg-default-50 p-3" data-banner-code>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-primary">HTML y CSS a medida</span>
        <div className="flex items-center gap-3">
          {empty && (
            <button
              type="button"
              className="text-xs text-primary hover:underline"
              data-banner-example
              onClick={() => setFormData((f) => ({ ...f, wideHtmlEs: BANNER_CODE_EXAMPLE.html, wideCss: BANNER_CODE_EXAMPLE.css }))}
            >
              Insertar un ejemplo
            </button>
          )}
          <button type="button" className="text-xs text-default-500 hover:text-foreground" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-1.5" data-banner-html>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-default-700">HTML</span>
            <div className="flex items-center gap-1">
              {['es', 'en'].map((code) => (
                <button
                  key={code}
                  type="button"
                  data-html-lang={code}
                  className={`rounded px-2 py-0.5 text-[11px] font-semibold uppercase ${htmlLang === code ? 'bg-primary text-white' : 'bg-default-200 text-default-600 hover:bg-default-300'}`}
                  onClick={() => setHtmlLang(code)}
                  title={code === 'es' ? 'El que ven los usuarios en español' : 'El que ve el resto. Si se deja vacío se usa el ES.'}
                >
                  {code}
                  {clean(formData[code === 'en' ? 'wideHtmlEn' : 'wideHtmlEs']) ? ' •' : ''}
                </button>
              ))}
            </div>
          </div>
          <textarea
            className={CODE_BOX}
            spellCheck={false}
            value={htmlValue}
            onChange={(event) => setFormData((f) => ({ ...f, [htmlKey]: event.target.value }))}
            placeholder={'<div class="mi-cinta">\n  <strong>-30 %</strong>\n</div>'}
          />
          {htmlTooLong && <p className="text-xs text-danger">Demasiado largo: como mucho {BannerViews.HTML_MAX} caracteres.</p>}
          {dropped.length > 0 && (
            <p className="text-xs text-warning-600" data-html-dropped>
              No se admite y se ha quitado: <CodeWords words={dropped} />
            </p>
          )}
          <p className="text-xs leading-relaxed text-default-500">
            Va en una capa (<code>.bn-html</code>) a todo el lienzo, encima del fondo y debajo de los textos y el botón, en las dos vistas.
            Ponle tus propias clases y dales forma en el CSS. {htmlLang === 'en' ? 'Sin HTML en EN se usa el ES.' : 'El EN es para el resto de idiomas; vacío, se usa este.'}
          </p>
        </div>

        <div className="flex min-w-0 flex-col gap-1.5" data-banner-css>
          <span className="text-xs font-semibold text-default-700">CSS</span>
          <textarea
            className={CODE_BOX}
            spellCheck={false}
            value={formData.wideCss || ''}
            onChange={(event) => setFormData((f) => ({ ...f, wideCss: event.target.value }))}
            placeholder={'.bn-text-2 {\n  letter-spacing: 0.02em;\n  text-transform: uppercase;\n}'}
          />
          {cssProblem && <p className="text-xs text-danger">{CSS_PROBLEMS[cssProblem]}</p>}
          <p className="text-xs leading-relaxed text-default-500">
            Solo afecta a este banner y se ve al momento en el lienzo. Color, grosor y alineación de los textos y del botón los pone el editor:
            para pisarlos hace falta <code>!important</code>.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-md border border-default-200 bg-default-100/60 p-3 text-xs leading-relaxed" data-banner-reference>
        <span className="text-xs font-semibold text-default-700">Nombres que puedes usar</span>
        <pre className="overflow-x-auto rounded bg-default-200/60 p-2 font-mono text-[11px] leading-relaxed text-default-700">{ref.structure.join('\n')}</pre>
        <div className="grid gap-4 lg:grid-cols-3">
          <RefList title="Clases (para el CSS)" rows={ref.classes} />
          <RefList title="Variables" rows={ref.variables} />
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-default-600">HTML admitido</span>
            <p className="text-default-500">
              <span className="text-default-700">Etiquetas:</span>{' '}
              <CodeWords words={ref.tags} />
            </p>
            <p className="text-default-500">
              <span className="text-default-700">Atributos:</span>{' '}
              <CodeWords words={ref.attrs.all} /> · en <code>img</code>: <CodeWords words={ref.attrs.img} /> · en <code>svg</code> y sus formas: <code>viewBox</code>, <code>d</code>, <code>fill</code>, <code>stroke</code>, <code>transform</code> y los de geometría.
            </p>
            <p className="text-default-500">
              Sin <code>style=""</code> (los estilos van en el CSS), sin <code>id</code>, sin enlaces ni scripts. Las imágenes, solo las subidas aquí
              (<code>https://storage.tikcontrol.live/…</code>). Tus clases, con tu propio nombre (<code>mi-…</code>): las <code>bn-</code> son las de arriba
              y las <code>hm-</code> se quitan.
            </p>
          </div>
        </div>
        <p className="text-default-500">
          <span className="text-default-700">Para que escale con el banner:</span> coloca con <code>%</code> (del lienzo) y da los tamaños con{' '}
          <code>calc(N * var(--hm-u))</code>, donde N son px del lienzo (de {BannerViews.WIDE.width} × {BannerViews.WIDE.height} en la vista grande y{' '}
          {BannerViews.SMALL.width} × {BannerViews.SMALL.height} en la pequeña). Dentro de <code>.bn-html</code>, <code>1em</code> = 16 px del lienzo.
          Para cambiar algo solo en una vista: <code>.bn-small .mi-clase {'{ … }'}</code>. En la pequeña deja libre un 8 % a cada lado: ahí van las
          flechas del carrusel. Un clic en cualquier parte del banner, también en tu HTML, hace la acción del botón. No se cargan fuentes ni imágenes de fuera.
        </p>
      </div>
    </div>
  );
}

// El banner del Inicio tiene dos vistas. La grande se diseña siempre; la
// pequeña sale sola de la grande hasta que se pulsa «Personalizar».
function BannerViewsEditor(props) {
  const { formData, setFormData, previewUrls, lang } = props;
  const [view, setView] = useState('wide');
  const [cssOpen, setCssOpen] = useState(false);
  const custom = !!formData.wideSmallCustom;
  const hasCss = !!clean(formData.wideCss) || !!clean(formData.wideHtmlEs) || !!clean(formData.wideHtmlEn);
  const cssPanel = cssOpen ? (
    <BannerCodePanel formData={formData} setFormData={setFormData} onClose={() => setCssOpen(false)} />
  ) : null;

  const switcher = (
    <div className="flex flex-wrap items-center gap-2" data-banner-views>
      <ToolChip active={view === 'wide'} onClick={() => setView('wide')} title="La que se ve con la ventana ancha">
        Vista grande
      </ToolChip>
      <ToolChip active={view === 'small'} onClick={() => setView('small')} title="La que se ve con la ventana estrecha">
        Vista pequeña
        <span className="font-normal opacity-70">{custom ? '· a medida' : '· automática'}</span>
      </ToolChip>
      <span className="text-xs text-default-500">
        La app enseña la pequeña cuando el banner mide menos de {BannerViews.SMALL_BELOW} px de ancho (ventana estrecha).
      </span>
      <span className="ml-auto" />
      <ToolChip active={cssOpen} onClick={() => setCssOpen((open) => !open)} title="HTML y CSS a medida para este banner">
        <span className="font-mono text-[11px]" aria-hidden="true">{'</>'}</span>
        HTML · CSS
        {hasCss && <span className="h-1.5 w-1.5 rounded-full bg-success" aria-label="con HTML o CSS a medida" />}
      </ToolChip>
    </div>
  );

  if (view === 'wide') return <CanvasEditor key="wide" {...props} cfg={WIDE_EDITOR} header={switcher} footer={cssPanel} />;

  function customize() {
    const derived = derivedSmallForm(formData);
    setFormData((f) => ({
      ...f,
      wideSmallCustom: true,
      wideSmallBackgroundEs: '',
      wideSmallBackgroundEn: '',
      wideSmallLayers: derived.layers,
      wideSmallCtaStyle: derived.ctaStyle,
      wideSmallCountdown: derived.countdown ? countdownPlace(derived.countdown) : null
    }));
  }

  function backToAuto() {
    if (!window.confirm('¿Descartar la vista pequeña a medida? Volverá a generarse sola a partir de la grande.')) return;
    setFormData((f) => ({ ...f, wideSmallCustom: false, wideSmallBackgroundEs: '', wideSmallBackgroundEn: '', wideSmallLayers: [], wideSmallCountdown: null }));
  }

  if (custom) {
    return (
      <CanvasEditor
        key="small"
        {...props}
        cfg={WIDE_SMALL_EDITOR}
        header={switcher}
        footer={(
          <>
          {cssPanel}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-default-200 pt-2">
            <p className="text-xs text-default-500">
              Esta vista ya no sigue a la grande: si cambias allí un texto, cámbialo también aquí.
            </p>
            <Button size="sm" variant="light" color="danger" className="h-8" onPress={backToAuto}>
              Volver a la automática
            </Button>
          </div>
          </>
        )}
      />
    );
  }

  const derived = derivedSmallForm(formData);
  const image = previewImage(previewUrls, formData, 'wideBackgroundEs', 'wideBackgroundEn', lang);
  const ctaText = getCtaText(formData.bannerCta, formData.bannerCtaEn, lang, formData.bannerCtaTranslations);
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-default-200 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <h3 className="text-sm font-semibold">{WIDE_SMALL_EDITOR.title}</h3>
          <span className="text-xs text-default-500">{WIDE_SMALL_EDITOR.hint}</span>
        </div>
        <Chip size="sm" variant="flat">{lang.toUpperCase()}</Chip>
      </div>
      {switcher}
      {/* Solo se mira: sale de la grande y cambia con ella. */}
      <div className="pointer-events-none" data-small-auto-preview>
        <VisualCanvas
          bare
          surface={PROMO_PREVIEW_SURFACES.wideBannerSmall}
          image={image}
          layers={derived.layers}
          fallbackLayer={WIDE_SMALL_EDITOR.fallbackLayer}
          lang={lang}
          cta={ctaText}
          ctaStyle={derived.ctaStyle}
          hooks="small"
          css={formData.wideCss}
          html={bannerHtmlFor(formData, lang)}
          countdown={derived.countdown}
          selected={null}
          onSelect={() => {}}
          onLayersChange={() => {}}
          onCtaStyleChange={() => {}}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-default-500">
          Automática: sale de la vista grande (mismos textos y botón, recolocados) y cambia con ella. Personalízala si quieres otra imagen u otra colocación.
        </p>
        <Button size="sm" variant="flat" color="primary" className="h-8" onPress={customize}>
          Personalizar vista pequeña
        </Button>
      </div>
      {cssPanel}
    </section>
  );
}

const MODAL_EDITOR = {
  title: 'Popup',
  hint: 'Vertical, 830 × 1306 px.',
  surface: PROMO_PREVIEW_SURFACES.modal,
  orientation: 'tall',
  snap: 1,
  fallbackLayer: DEFAULT_MODAL_LAYER,
  layersKey: 'modalLayers',
  ctaStyleKey: 'modalCtaStyle',
  ctaKey: 'modalCta',
  ctaEnKey: 'modalCtaEn',
  ctaTranslationsKey: 'modalCtaTranslations',
  bgEsKey: 'modalImageEs',
  bgEnKey: 'modalImageEn',
  bgHint: 'Imagen de 830 × 1306 px. Si no hay imagen EN se usa la ES.',
  uploadKind: 'popup',
  withChip: false,
  allowHidden: false
};

// Banner vertical + su version pequeña: solo los pintan las versiones de la
// app anteriores al nuevo Inicio.
function LegacyBannerPreview({ formData, previewUrls, lang, setFormData }) {
  const bannerImage = previewImage(previewUrls, formData, 'bannerBackgroundEs', 'bannerBackgroundEn', lang);
  const compactImage = lang === 'en'
    ? (previewUrls.bannerCompactBackgroundEn || formData.bannerCompactBackgroundEn || previewUrls.bannerBackgroundEn || formData.bannerBackgroundEn || previewUrls.bannerBackgroundEs || formData.bannerBackgroundEs)
    : (previewUrls.bannerCompactBackgroundEs || formData.bannerCompactBackgroundEs || previewUrls.bannerBackgroundEs || formData.bannerBackgroundEs || previewUrls.bannerBackgroundEn || formData.bannerBackgroundEn);
  const bannerCta = getCtaText(formData.bannerCta, formData.bannerCtaEn, lang, formData.bannerCtaTranslations);

  return (
    <aside className="sticky top-0 flex max-h-[calc(100vh-150px)] flex-col gap-4 self-start overflow-auto rounded-xl border border-default-200 bg-default-50 p-4">
      <VisualCanvas
        title="Banner grande"
        surface={PROMO_PREVIEW_SURFACES.desktopBanner}
        image={bannerImage}
        layers={formData.bannerLayers}
        lang={lang}
        cta={bannerCta}
        ctaStyle={formData.bannerCtaStyle}
        hint="Formato anterior: panel lateral vertical de Inicio."
        onLayersChange={(bannerLayers) => setFormData((f) => ({ ...f, bannerLayers }))}
        onCtaStyleChange={(bannerCtaStyle) => setFormData((f) => ({ ...f, bannerCtaStyle }))}
      />
      <VisualCanvas
        title="Banner pequeno"
        surface={PROMO_PREVIEW_SURFACES.compactBanner}
        image={compactImage}
        layers={formData.compactLayers}
        lang={lang}
        cta={bannerCta}
        ctaStyle={formData.compactCtaStyle}
        hint="Formato anterior: pantallas pequenas."
        onLayersChange={(compactLayers) => setFormData((f) => ({ ...f, compactLayers }))}
        onCtaStyleChange={(compactCtaStyle) => setFormData((f) => ({ ...f, compactCtaStyle }))}
      />
    </aside>
  );
}

async function markCloudConfigured() {
  await setDoc(
    doc(db, 'popups', '_meta'),
    { enabled: true, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

function revokePreviewUrls(urls, keep = new Set()) {
  Object.values(urls || {}).forEach((url) => {
    if (typeof url === 'string' && url.startsWith('blob:') && !keep.has(url)) URL.revokeObjectURL(url);
  });
}

export default function Popups() {
  const { canWrite } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [previewLang, setPreviewLang] = useState('es');
  const [sortDescriptor, setSortDescriptor] = useState({
    column: 'priority',
    direction: 'descending'
  });
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [previewUrls, setPreviewUrls] = useState({});
  const previewUrlsRef = useRef(previewUrls);
  const uploadedPreviewByUrlRef = useRef({});
  const [editingId, setEditingId] = useState(null);
  const [duplicatingFrom, setDuplicatingFrom] = useState(null);
  // Se ha intentado crear o subir una imagen sin ID: el campo se marca en rojo.
  const [idMissing, setIdMissing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState('');
  const [audienceCount, setAudienceCount] = useState(null);
  const [audienceCountLoading, setAudienceCountLoading] = useState(false);
  const audienceCountSeqRef = useRef(0);
  const whitelistLoadSeqRef = useRef(0);
  const [audienceReach, setAudienceReach] = useState({});
  const audienceReachRequestedRef = useRef(new Set());
  const [popupStats, setPopupStats] = useState({});
  const [popupStatsState, setPopupStatsState] = useState('loading');
  const [statsRange, setStatsRange] = useState('all');
  const [translationPrompt, setTranslationPrompt] = useState('');
  const [translationJson, setTranslationJson] = useState('');
  const [translationError, setTranslationError] = useState('');
  const form = useDisclosure();
  const translationDialog = useDisclosure();

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, 'popups'), orderBy('__name__'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.filter((d) => !d.id.startsWith('_')).map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Error cargando popups');
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  useEffect(() => {
    previewUrlsRef.current = previewUrls;
  }, [previewUrls]);

  useEffect(() => () => revokePreviewUrls(previewUrlsRef.current), []);

  // Alcance por card: una llamada por firma de audiencia distinta (los popups
  // sin filtros comparten la misma), cacheada de por vida de la vista. Si el
  // endpoint falla (worker aun sin desplegar), la card omite el numero.
  useEffect(() => {
    items.forEach((item) => {
      const signature = audienceSignatureForItem(item);
      if (audienceReachRequestedRef.current.has(signature)) return;
      audienceReachRequestedRef.current.add(signature);
      (async () => {
        try {
          const res = await apiFetch('/api/backoffice/popups/audience-count', {
            method: 'POST',
            body: { audience: JSON.parse(signature) }
          });
          setAudienceReach((prev) => ({ ...prev, [signature]: res }));
        } catch (_) {
          setAudienceReach((prev) => ({ ...prev, [signature]: null }));
        }
      })();
    });
  }, [items]);

  // Impresiones/clicks de todos los popups en una sola llamada. Si el Worker
  // aun no tiene la ruta (404) o falta la migracion, las cards lo indican.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/backoffice/popups/stats');
        if (cancelled) return;
        if (res?.ok && res.available !== false) {
          setPopupStats(res.popups || {});
          setPopupStatsState('ready');
        } else {
          setPopupStatsState('unavailable');
        }
      } catch (_) {
        if (!cancelled) setPopupStatsState('unavailable');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Alcance de la audiencia: el worker cuenta contra su copia cacheada de la
  // coleccion users (~5 min). Debounce para no disparar en cada click, y un
  // numero de secuencia para descartar respuestas que llegan tarde.
  const whitelistParsed = useMemo(() => PromoWhitelist.parseList(formData.audienceWhitelist), [formData.audienceWhitelist]);
  const audienceSignature = JSON.stringify({
    audience: buildAudiencePayload(formData),
    whitelistEntries: whitelistParsed.entries.map((entry) => entry.raw)
  });
  useEffect(() => {
    if (!form.isOpen) return undefined;
    const seq = ++audienceCountSeqRef.current;
    setAudienceCountLoading(true);
    const timer = setTimeout(async () => {
      try {
        const sig = JSON.parse(audienceSignature);
        const res = await apiFetch('/api/backoffice/popups/audience-count', {
          method: 'POST',
          body: sig.whitelistEntries.length ? sig : { audience: sig.audience }
        });
        if (audienceCountSeqRef.current === seq) setAudienceCount(res);
      } catch (e) {
        if (audienceCountSeqRef.current === seq) setAudienceCount({ error: e.message });
      } finally {
        if (audienceCountSeqRef.current === seq) setAudienceCountLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.isOpen, audienceSignature]);

  // Con el switch apagado, solo los que no están archivados; encendido, solo
  // los archivados.
  const archivedCount = useMemo(() => items.filter(isArchivedPopup).length, [items]);
  const listTotal = showArchived ? archivedCount : items.length - archivedCount;

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = items.filter((item) => {
      if (isArchivedPopup(item) !== showArchived) return false;
      if (!needle) return true;
      return [
        item.id,
        item.banner?.title,
        item.banner?.overlay?.html,
        ...(item.banner?.overlays || []).map((layer) => layer?.html),
        ...(item.banner?.wide?.overlays || []).map((layer) => layer?.html),
        item.modal?.overlay?.html,
        ...(item.modal?.overlays || []).map((layer) => layer?.html)
      ].join(' ').toLowerCase().includes(needle);
    });
    return applySort(base, sortDescriptor, {
      id: (x) => x.id,
      title: (x) => (x.banner?.title || x.id || '').toLowerCase(),
      priority: (x) => Number(x.priority || 0),
      state: (x) => (x.active === false ? 1 : 0),
      visibility: (x) => (x.adminOnly || x.visibility === 'admins' ? 0 : 1),
      expiresAt: (x) => tsToDate(x.expiresAt)?.getTime() || 0
    });
  }, [items, search, sortDescriptor, showArchived]);

  // ── Arrastrar para cambiar la prioridad ──────────────────────────────
  // Solo con la lista completa ordenada por prioridad (de mayor a menor):
  // con busqueda o con otro orden, mover una card no tendria un sitio claro.
  // Al soltar se reasignan prioridades a TODA la lista (n, n-1, …, 1: la
  // primera card es la de mayor prioridad) y se escriben en un batch solo
  // los docs cuyo valor cambia. Los archivados no se ordenan (ni cuentan).
  const canReorder = canWrite
    && sortDescriptor.column === 'priority'
    && sortDescriptor.direction === 'descending'
    && !search.trim()
    && !showArchived;
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [reordering, setReordering] = useState(false);

  function onCardDragStart(event, item) {
    if (!canReorder) return;
    setDragId(item.id);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', item.id);
  }

  function onCardDragOver(event, item) {
    if (!canReorder || !dragId || dragId === item.id) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dragOverId !== item.id) setDragOverId(item.id);
  }

  function onCardDragEnd() {
    setDragId(null);
    setDragOverId(null);
  }

  async function onCardDrop(event, target) {
    event.preventDefault();
    const sourceId = dragId || event.dataTransfer.getData('text/plain');
    onCardDragEnd();
    if (!canReorder || !sourceId || sourceId === target.id) return;
    const order = filtered.map((item) => item.id);
    const from = order.indexOf(sourceId);
    const to = order.indexOf(target.id);
    if (from < 0 || to < 0) return;
    order.splice(from, 1);
    order.splice(to, 0, sourceId);

    const byId = new Map(items.map((item) => [item.id, item]));
    const batch = writeBatch(db);
    let changed = 0;
    order.forEach((id, index) => {
      const priority = order.length - index;
      if (Number(byId.get(id)?.priority || 0) === priority) return;
      batch.update(doc(db, 'popups', id), { priority, updatedAt: serverTimestamp() });
      changed++;
    });
    if (!changed) return;
    setReordering(true);
    try {
      await batch.commit();
      setToast({ type: 'ok', msg: `Prioridad actualizada (${changed} popup${changed === 1 ? '' : 's'}).` });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setReordering(false);
    }
  }

  function openNew() {
    setPreviewUrls((prev) => {
      revokePreviewUrls(prev, new Set(Object.values(uploadedPreviewByUrlRef.current)));
      return {};
    });
    setFormData({
      ...EMPTY_FORM,
      wideLayers: WIDE_STARTER_LAYERS.map((layer) => cloneLayer(DEFAULT_WIDE_LAYER, layer)),
      wideCtaStyle: { ...DEFAULT_WIDE_CTA_STYLE },
      bannerLayers: [cloneLayer(DEFAULT_LAYER, { html: '<strong>Nueva actualizacion</strong>' })],
      compactLayers: [cloneLayer(DEFAULT_COMPACT_LAYER, { html: '<strong>Novedad</strong>' })],
      modalLayers: [cloneLayer(DEFAULT_MODAL_LAYER, { html: '<strong>Nueva actualizacion</strong>' })],
      bannerCtaStyle: { ...EMPTY_FORM.bannerCtaStyle },
      compactCtaStyle: { ...EMPTY_FORM.compactCtaStyle },
      modalCtaStyle: { ...EMPTY_FORM.modalCtaStyle }
    });
    setEditingId(null);
    setDuplicatingFrom(null);
    setIdMissing(false);
    form.onOpen();
  }

  function openForItem(item, { duplicate = false } = {}) {
    const nextForm = toForm(item);
    if (duplicate) {
      nextForm.id = nextCopyId(item.id, new Set(items.map((existing) => existing.id)));
      nextForm.active = false;
    }
    setPreviewUrls((prev) => {
      const cachedUrls = new Set(Object.values(uploadedPreviewByUrlRef.current));
      revokePreviewUrls(prev, cachedUrls);
      return {
        ...(uploadedPreviewByUrlRef.current[nextForm.bannerBackgroundEs] ? { bannerBackgroundEs: uploadedPreviewByUrlRef.current[nextForm.bannerBackgroundEs] } : {}),
        ...(uploadedPreviewByUrlRef.current[nextForm.bannerBackgroundEn] ? { bannerBackgroundEn: uploadedPreviewByUrlRef.current[nextForm.bannerBackgroundEn] } : {}),
        ...(uploadedPreviewByUrlRef.current[nextForm.bannerCompactBackgroundEs] ? { bannerCompactBackgroundEs: uploadedPreviewByUrlRef.current[nextForm.bannerCompactBackgroundEs] } : {}),
        ...(uploadedPreviewByUrlRef.current[nextForm.bannerCompactBackgroundEn] ? { bannerCompactBackgroundEn: uploadedPreviewByUrlRef.current[nextForm.bannerCompactBackgroundEn] } : {}),
        ...(uploadedPreviewByUrlRef.current[nextForm.wideBackgroundEs] ? { wideBackgroundEs: uploadedPreviewByUrlRef.current[nextForm.wideBackgroundEs] } : {}),
        ...(uploadedPreviewByUrlRef.current[nextForm.wideBackgroundEn] ? { wideBackgroundEn: uploadedPreviewByUrlRef.current[nextForm.wideBackgroundEn] } : {}),
        ...(uploadedPreviewByUrlRef.current[nextForm.wideSmallBackgroundEs] ? { wideSmallBackgroundEs: uploadedPreviewByUrlRef.current[nextForm.wideSmallBackgroundEs] } : {}),
        ...(uploadedPreviewByUrlRef.current[nextForm.wideSmallBackgroundEn] ? { wideSmallBackgroundEn: uploadedPreviewByUrlRef.current[nextForm.wideSmallBackgroundEn] } : {}),
        ...(uploadedPreviewByUrlRef.current[nextForm.modalImageEs] ? { modalImageEs: uploadedPreviewByUrlRef.current[nextForm.modalImageEs] } : {}),
        ...(uploadedPreviewByUrlRef.current[nextForm.modalImageEn] ? { modalImageEn: uploadedPreviewByUrlRef.current[nextForm.modalImageEn] } : {})
      };
    });
    setFormData(nextForm);
    setEditingId(duplicate ? null : item.id);
    setDuplicatingFrom(duplicate ? item.id : null);
    setIdMissing(false);
    form.onOpen();
    // La lista blanca escrita tal cual está aparte (solo ADMIN). Si no se
    // puede leer, se conservan las huellas que ya tenía el doc.
    const seq = ++whitelistLoadSeqRef.current;
    if (nextForm.audienceWhitelistHashes.length) {
      getDoc(doc(db, 'popups_private', item.id))
        .then((snap) => {
          if (whitelistLoadSeqRef.current !== seq || !snap.exists()) return;
          const list = Array.isArray(snap.data()?.whitelist) ? snap.data().whitelist.map(String) : [];
          setFormData((f) => ({ ...f, audienceWhitelist: list.join('\n'), audienceWhitelistLoaded: true }));
        })
        .catch(() => { /* reglas sin publicar o sin permiso: se quedan las huellas */ });
    }
  }

  function openEdit(item) {
    openForItem(item);
  }

  function openDuplicate(item) {
    openForItem(item, { duplicate: true });
  }

  function setImageField(field, value) {
    setPreviewUrls((prev) => {
      const cachedUrls = new Set(Object.values(uploadedPreviewByUrlRef.current));
      if (prev[field] && !cachedUrls.has(prev[field])) URL.revokeObjectURL(prev[field]);
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setFormData((f) => ({ ...f, [field]: value }));
  }

  function setLocalPreview(field, file) {
    const localUrl = URL.createObjectURL(file);
    setPreviewUrls((prev) => {
      const cachedUrls = new Set(Object.values(uploadedPreviewByUrlRef.current));
      if (prev[field] && !cachedUrls.has(prev[field])) URL.revokeObjectURL(prev[field]);
      return { ...prev, [field]: localUrl };
    });
    return localUrl;
  }

  // El ID se escribe siempre a mano: ya no se saca del texto del banner (salía
  // «nueva-actualizacion» en todos). Las imágenes se suben a la carpeta del ID.
  async function uploadAsset(field, kind, lang, file) {
    const popupId = slugify(editingId || formData.id);
    if (!popupId) {
      setIdMissing(true);
      setToast({ type: 'error', msg: 'Escribe el ID del popup antes de subir imágenes.' });
      return;
    }
    setUploadingField(field);
    // Se sube ya a la medida de su lienzo (si es más pequeña, tal cual).
    const fitted = await shrinkToSurface(file, UPLOAD_SURFACES[field]);
    const localPreviewUrl = setLocalPreview(field, fitted.file);
    try {
      const body = new FormData();
      body.append('file', fitted.file);
      body.append('kind', kind);
      body.append('lang', lang);
      const res = await apiUpload(`/api/backoffice/popups/${encodeURIComponent(popupId)}/assets`, body);
      setFormData((f) => ({
        ...f,
        id: f.id || popupId,
        [field]: res.url
      }));
      uploadedPreviewByUrlRef.current[res.url] = localPreviewUrl;
      setToast({
        type: 'ok',
        msg: fitted.resized
          ? `Imagen subida a R2, reducida de ${fitted.from.width} × ${fitted.from.height} a ${fitted.to.width} × ${fitted.to.height} px.`
          : 'Imagen subida a R2.'
      });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setUploadingField('');
    }
  }

  function translationFieldsFromForm() {
    const fields = { bannerCta: formData.bannerCta };
    (formData.wideLayers || []).forEach((layer, index) => {
      const key = index === 0 ? 'wideHtml' : `wideHtml${index + 1}`;
      fields[key] = layer.html;
    });
    // La vista pequeña a medida lleva sus propios textos.
    (formData.wideSmallCustom ? formData.wideSmallLayers || [] : []).forEach((layer, index) => {
      const key = index === 0 ? 'wideSmallHtml' : `wideSmallHtml${index + 1}`;
      fields[key] = layer.html;
    });
    (formData.bannerLayers || []).forEach((layer, index) => {
      const key = index === 0 ? 'bannerHtml' : `bannerHtml${index + 1}`;
      fields[key] = layer.html;
    });
    (formData.compactLayers || []).forEach((layer, index) => {
      const key = index === 0 ? 'compactHtml' : `compactHtml${index + 1}`;
      fields[key] = layer.html;
    });
    if (formData.hasPopup) {
      (formData.modalLayers || []).forEach((layer, index) => {
        const key = index === 0 ? 'modalHtml' : `modalHtml${index + 1}`;
        fields[key] = layer.html;
      });
      fields.modalCta = formData.modalCta;
    }
    const filledFields = Object.fromEntries(
      Object.entries(fields).filter(([, value]) => clean(value))
    );
    return filledFields;
  }

  function openTranslationAssistant() {
    const filledFields = translationFieldsFromForm();
    if (!Object.keys(filledFields).length) {
      setToast({ type: 'error', msg: 'No hay texto ES para traducir.' });
      return;
    }
    const targetLanguages = TRANSLATION_LANGS
      .filter((lang) => lang.key !== 'es')
      .map((lang) => ({ code: lang.key, name: lang.name }));
    const prompt = [
      'Traduce estos textos promocionales de TikControl desde español al resto de idiomas.',
      '',
      'Devuelve SOLO JSON valido, dentro de un bloque de codigo y sin explicaciones, con esta forma exacta:',
      '{"translations":{"fieldKey":{"en":"...","fr":"..."}}}',
      '',
      'Reglas:',
      '- Mantén exactamente las mismas claves de campo.',
      '- Usa exactamente estos codigos de idioma: ' + targetLanguages.map((lang) => lang.code).join(', '),
      '- Conserva las etiquetas HTML permitidas como etiquetas: <strong>, <b>, <em>, <i>, <u>, <span>, <small>, <p>, <div>, <mark>, <sup>, <sub>, <br>.',
      '- Escribe esas etiquetas tal cual y en el mismo sitio del texto (por ejemplo <br> y <span>...</span>): sin barra invertida delante (nada de \\<br> ni \\<span>) y sin convertirlas en &lt; &gt;.',
      '- No anadas links, scripts, markdown ni claves extra.',
      '- No uses comillas dobles rectas (") dentro de los textos: usa comillas tipograficas (« », “ ”) o escapalas como \\".',
      '- Los CTA deben ser cortos y naturales para botones.',
      '',
      JSON.stringify({ sourceLanguage: 'es', targetLanguages, fields: filledFields }, null, 2)
    ].join('\n');

    setTranslationPrompt(prompt);
    setTranslationJson('');
    setTranslationError('');
    translationDialog.onOpen();
  }

  async function copyTranslationPrompt() {
    try {
      await navigator.clipboard.writeText(translationPrompt);
      setToast({ type: 'ok', msg: 'Prompt copiado.' });
    } catch (e) {
      setToast({ type: 'error', msg: 'No se pudo copiar: ' + e.message });
    }
  }

  function applyPastedTranslations() {
    let parsed;
    try {
      parsed = extractJsonObject(translationJson);
    } catch (e) {
      setTranslationError(e.message);
      return;
    }
    setTranslationError('');

    const t = parsed.translations || parsed;
    function applyLayerTranslations(layers, prefix) {
      return (layers || []).map((layer, index) => {
        const key = index === 0 ? `${prefix}Html` : `${prefix}Html${index + 1}`;
        const translated = t[key] || {};
        return {
          ...layer,
          htmlEn: translated.en || layer.htmlEn,
          translations: { ...(layer.translations || {}), es: layer.html, ...translated }
        };
      });
    }

    setFormData((f) => ({
      ...f,
      wideLayers: applyLayerTranslations(f.wideLayers, 'wide'),
      wideSmallLayers: applyLayerTranslations(f.wideSmallLayers, 'wideSmall'),
      bannerLayers: applyLayerTranslations(f.bannerLayers, 'banner'),
      compactLayers: applyLayerTranslations(f.compactLayers, 'compact'),
      modalLayers: applyLayerTranslations(f.modalLayers, 'modal'),
      bannerCtaEn: t.bannerCta?.en || f.bannerCtaEn,
      bannerCtaTranslations: { ...(f.bannerCtaTranslations || {}), es: f.bannerCta, ...(t.bannerCta || {}) },
      modalCtaEn: t.modalCta?.en || f.modalCtaEn,
      modalCtaTranslations: { ...(f.modalCtaTranslations || {}), es: f.modalCta, ...(t.modalCta || {}) }
    }));
    translationDialog.onClose();
    setToast({ type: 'ok', msg: 'Traducciones aplicadas.' });
  }

  function importBannerTexts(sourceKey, targetKey, fallback) {
    setFormData((f) => ({
      ...f,
      [targetKey]: importLayerTexts(f[sourceKey], f[targetKey], fallback)
    }));
  }

  async function saveForm() {
    const id = editingId || slugify(formData.id);
    if (!id) {
      setIdMissing(true);
      setToast({ type: 'error', msg: 'El ID es obligatorio: escríbelo arriba del todo.' });
      return;
    }
    if (!editingId && items.some((item) => item.id === id)) {
      setToast({ type: 'error', msg: `Ya existe un popup con el ID "${id}". Cambia el ID para crear uno nuevo.` });
      return;
    }
    if (isOpenUrlAction(formData.action) && !/^https?:\/\//i.test(actionUrl(formData.action))) {
      setToast({ type: 'error', msg: 'El enlace del CTA debe empezar por http:// o https://.' });
      return;
    }
    if (isOpenPopupAction(formData.action)) {
      const targetId = actionPopupId(formData.action);
      if (!targetId) {
        setToast({ type: 'error', msg: 'Selecciona el popup destino del CTA.' });
        return;
      }
      if (targetId === id) {
        setToast({ type: 'error', msg: 'El CTA no puede abrir el mismo popup.' });
        return;
      }
    }
    if (formData.audienceRegisteredAfter && formData.audienceRegisteredBefore &&
        formData.audienceRegisteredAfter > formData.audienceRegisteredBefore) {
      setToast({ type: 'error', msg: 'En Audiencia, "Alta desde" no puede ser posterior a "Alta hasta".' });
      return;
    }
    const wl = PromoWhitelist.parseList(formData.audienceWhitelist);
    if (wl.invalid.length) {
      setToast({ type: 'error', msg: `En la lista blanca hay entradas que no son un correo, un @usuario de TikTok ni un UID: ${wl.invalid.slice(0, 5).join(', ')}${wl.invalid.length > 5 ? '…' : ''}` });
      return;
    }
    if (formData.wideCountdown?.enabled && !Number.isFinite(Number(formData.wideCountdown.at))) {
      setToast({ type: 'error', msg: 'Pon la fecha y la hora en que termina la cuenta atrás (o quítala).' });
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload({ ...formData, id });
      // Lista blanca: en el doc (que lee cualquiera con sesión) solo sus
      // huellas. Sin texto escrito pero con una lista que no se pudo leer, se
      // conservan las huellas que ya tenía.
      let wlHashes = [];
      if (wl.entries.length) wlHashes = await PromoWhitelist.hashKeys(wl.entries.map((entry) => entry.key));
      else if (!formData.audienceWhitelistLoaded) wlHashes = formData.audienceWhitelistHashes || [];
      if (wlHashes.length) {
        payload.audience = { ...(payload.audience || {}), whitelist: wlHashes };
        // Las versiones anteriores de la app no entienden la lista: como «solo
        // admins» no la enseñan a todo el mundo. La nueva sabe que esa marca
        // viene de la lista (adminOnlyByWhitelist) y no de «Solo admins».
        payload.adminOnlyByWhitelist = !formData.adminOnly;
        payload.adminOnly = true;
        payload.visibility = 'admins';
      } else {
        payload.adminOnlyByWhitelist = false;
      }
      // La lista blanca no tiene tope de entradas; el único límite es el de
      // Firestore (1 MB por documento). Se comprueba antes de escribir nada,
      // en el banner (huellas) y en su copia privada (el texto).
      const tooBig = whitelistTooBig(payload, wl.entries.length ? { whitelist: wl.entries.map((entry) => entry.raw) } : null);
      if (tooBig) {
        setToast({
          type: 'error',
          msg: `La lista blanca no cabe en un banner: Firestore guarda como mucho 1 MB por documento y con ${wlHashes.length.toLocaleString('es-ES')} entradas se pasa. Quita unas ${tooBig.toLocaleString('es-ES')} o repártela en dos banners.`
        });
        return;
      }
      // Un archivado que se activa desde el editor vuelve a la lista: si no,
      // saldría en la app sin verse aquí.
      const unarchived = !!editingId && payload.active
        && isArchivedPopup(items.find((item) => item.id === editingId));
      if (unarchived) payload.archived = false;
      if (editingId) {
        await updateDoc(doc(db, 'popups', editingId), payload);
      } else {
        payload.createdAt = serverTimestamp();
        payload.createdBy = auth.currentUser?.uid || null;
        await setDoc(doc(db, 'popups', id), payload);
      }
      // El texto de la lista, aparte (solo ADMIN). Vaciada, se borra.
      let privateFailed = false;
      if (wl.entries.length) {
        await setDoc(doc(db, 'popups_private', id), {
          whitelist: wl.entries.map((entry) => entry.raw),
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || null
        }).catch(() => { privateFailed = true; });
      } else if (formData.audienceWhitelistLoaded) {
        await deleteDoc(doc(db, 'popups_private', id)).catch(() => {});
      }
      await markCloudConfigured();
      if (privateFailed) {
        setToast({ type: 'warning', msg: 'Guardado y ya filtra por la lista blanca, pero su texto no se ha podido guardar aparte (¿reglas de Firestore sin publicar?): al volver a abrirlo no verás los nombres.' });
        form.onClose();
        return;
      }
      setToast({
        type: 'ok',
        msg: unarchived
          ? 'Popup actualizado y desarchivado: está activo.'
          : (editingId ? 'Popup actualizado.' : (duplicatingFrom ? 'Popup duplicado.' : 'Popup creado.'))
      });
      form.onClose();
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(item) {
    try {
      await updateDoc(doc(db, 'popups', item.id), {
        active: item.active === false,
        updatedAt: serverTimestamp()
      });
      await markCloudConfigured();
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    }
  }

  // Archivar no borra nada: lo deja inactivo y fuera de la lista. Desarchivar
  // lo devuelve a la lista, todavía inactivo.
  async function setArchived(item, archived) {
    const name = item.banner?.title || item.id;
    if (archived && item.active !== false
        && !confirm(`"${name}" está activo. Al archivarlo se desactiva y deja de salir en la app. ¿Archivar?`)) return;
    try {
      await updateDoc(doc(db, 'popups', item.id), archived
        ? {
          archived: true,
          active: false,
          archivedAt: serverTimestamp(),
          archivedBy: auth.currentUser?.uid || null,
          updatedAt: serverTimestamp()
        }
        : { archived: false, updatedAt: serverTimestamp() });
      await markCloudConfigured();
      setToast({
        type: 'ok',
        msg: archived
          ? `"${name}" archivado. Lo tienes en «Ver archivados».`
          : `"${name}" desarchivado. Sigue inactivo hasta que lo actives.`
      });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    }
  }

  async function handleDelete(item) {
    if (!confirm(`Eliminar "${item.banner?.title || item.id}"?`)) return;
    try {
      await deleteDoc(doc(db, 'popups', item.id));
      await deleteDoc(doc(db, 'popups_private', item.id)).catch(() => {});
      await markCloudConfigured();
      setToast({ type: 'ok', msg: 'Popup eliminado.' });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Popups</h1>
          <div className="text-sm text-default-500">
            {loading
              ? 'Cargando...'
              : showArchived
                ? `${filtered.length} de ${listTotal} archivados`
                : `${filtered.length} de ${listTotal} popups${archivedCount ? ` · ${archivedCount} archivados` : ''}`}
          </div>
        </div>
        {canWrite && (
          <Button color="primary" onPress={openNew}>
            Nuevo popup visual
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Buscar por ID o texto..."
          value={search}
          onValueChange={setSearch}
          isClearable
          size="sm"
          className="max-w-sm"
        />
        <Select
          aria-label="Ordenar popups"
          size="sm"
          className="max-w-[180px]"
          disallowEmptySelection
          selectedKeys={[`${sortDescriptor.column}:${sortDescriptor.direction}`]}
          onSelectionChange={(keys) => {
            const [column, direction] = String(Array.from(keys)[0] || 'priority:descending').split(':');
            setSortDescriptor({ column, direction });
          }}
        >
          {CARD_SORT_OPTIONS.map((option) => <SelectItem key={option.key}>{option.label}</SelectItem>)}
        </Select>
        <Select
          aria-label="Rango de estadisticas"
          size="sm"
          className="max-w-[150px]"
          disallowEmptySelection
          selectedKeys={[statsRange]}
          onSelectionChange={(keys) => setStatsRange(String(Array.from(keys)[0] || 'all'))}
          startContent={<span className="text-[11px] text-default-400">Stats</span>}
        >
          {STATS_RANGE_OPTIONS.map((option) => <SelectItem key={option.key}>{option.label}</SelectItem>)}
        </Select>
        <Switch size="sm" isSelected={showArchived} onValueChange={setShowArchived}>
          <span className="text-xs">Ver archivados ({archivedCount})</span>
        </Switch>
        {canWrite && !showArchived && (
          <span className="text-xs text-default-400">
            {canReorder
              ? 'Arrastra una card para cambiar su prioridad.'
              : 'Ordena por prioridad y sin busqueda para arrastrar y cambiar la prioridad.'}
          </span>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </div>
      )}
      {toast && (
        <div className={`rounded-md border p-3 text-sm ${toast.type === 'ok' ? 'border-success-200 bg-success-50 text-success-700' : 'border-danger-200 bg-danger-50 text-danger-700'}`}>
          <div className="flex items-center justify-between gap-3">
            <span>{toast.msg}</span>
            <button className="text-xs underline" onClick={() => setToast(null)}>cerrar</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center p-8"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-default-200 p-8 text-center text-sm text-default-400">
          {search.trim()
            ? 'Ningún popup coincide con la búsqueda.'
            : showArchived ? 'No hay popups archivados.' : 'No hay popups en Firestore.'}
        </div>
      ) : (
        <div className={`grid gap-4 md:grid-cols-2 2xl:grid-cols-3 ${reordering ? 'pointer-events-none opacity-60' : ''}`}>
          {filtered.map((item) => (
            <div
              key={item.id}
              draggable={canReorder}
              onDragStart={(event) => onCardDragStart(event, item)}
              onDragOver={(event) => onCardDragOver(event, item)}
              onDrop={(event) => onCardDrop(event, item)}
              onDragEnd={onCardDragEnd}
              className={`rounded-xl transition ${canReorder ? 'cursor-grab active:cursor-grabbing' : ''} ${
                dragId === item.id ? 'opacity-40' : ''
              } ${dragOverId === item.id ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''}`}
            >
            <PopupCard
              item={item}
              reach={audienceReach[audienceSignatureForItem(item)]}
              stats={popupStats[item.id]}
              statsState={popupStatsState}
              statsRange={statsRange}
              canWrite={canWrite}
              onEdit={() => openEdit(item)}
              onDuplicate={() => openDuplicate(item)}
              onDelete={() => handleDelete(item)}
              onToggleActive={() => toggleActive(item)}
              onArchive={() => setArchived(item, true)}
              onUnarchive={() => setArchived(item, false)}
            />
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={form.isOpen} onClose={form.onClose} size="full" scrollBehavior="inside">
        <ModalContent>
          <ModalHeader>
            {editingId
              ? 'Editar popup visual'
              : (duplicatingFrom ? `Duplicar popup (copia de "${duplicatingFrom}")` : 'Nuevo popup visual')}
          </ModalHeader>
          <ModalBody className="flex flex-col gap-4">
            <div className="grid gap-3 md:grid-cols-4">
              <Input
                label="ID"
                isRequired={!editingId}
                placeholder={editingId ? undefined : 'ej. halloween-2026'}
                value={formData.id}
                isDisabled={!!editingId}
                isInvalid={!editingId && idMissing && !slugify(formData.id)}
                errorMessage="Escribe un ID: letras, números y guiones."
                onValueChange={(v) => setFormData((f) => ({ ...f, id: typingSlug(v) }))}
              />
              <Input
                label="Prioridad"
                type="number"
                value={String(formData.priority)}
                onValueChange={(v) => setFormData((f) => ({ ...f, priority: v }))}
              />
              <Input
                label="Expira"
                type="date"
                value={formData.expiresAt}
                onValueChange={(v) => setFormData((f) => ({ ...f, expiresAt: v }))}
              />
              <Select
                label="Preview"
                selectedKeys={[previewLang]}
                onSelectionChange={(keys) => setPreviewLang(String(Array.from(keys)[0] || 'es'))}
              >
                {TRANSLATION_LANGS.map((lang) => <SelectItem key={lang.key}>{lang.label}</SelectItem>)}
              </Select>
            </div>

            {/* Banner del Inicio: lienzo a todo el ancho; lo de cada texto, el boton y la imagen, en su panel. */}
            <BannerViewsEditor
              formData={formData}
              previewUrls={previewUrls}
              lang={previewLang}
              setFormData={setFormData}
              uploadingField={uploadingField}
              onImageChange={setImageField}
              onUpload={uploadAsset}
            />

            <div className={`grid items-start gap-4 ${formData.hasPopup ? 'xl:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]' : ''}`}>
              <div className="flex flex-col gap-4">
                <div className="rounded-xl border border-default-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold">Traducciones</h3>
                      <p className="text-xs text-default-500">Copia un prompt para ChatGPT y pega aqui el JSON traducido.</p>
                    </div>
                    <Button variant="flat" color="primary" onPress={openTranslationAssistant}>
                      Preparar prompt ChatGPT
                    </Button>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  {formData.hasPopup ? (
                    <Select
                      label="Accion CTA popup"
                      selectedKeys={[selectedActionKey(formData.action, ACTION_OPTIONS, 'dismiss')]}
                      onSelectionChange={(keys) => {
                        const key = String(Array.from(keys)[0] || 'dismiss');
                        setFormData((f) => ({
                          ...f,
                          action: key === 'openUrl'
                            ? normalizeUrlAction(actionUrl(f.action))
                            : key === 'openPopup'
                              ? normalizePopupAction(actionPopupId(f.action))
                              : key
                        }));
                      }}
                    >
                      {ACTION_OPTIONS.map((a) => <SelectItem key={a.key}>{a.label}</SelectItem>)}
                    </Select>
                  ) : (
                    <Select
                      label="Accion CTA banner"
                      selectedKeys={[selectedActionKey(formData.action, BANNER_ACTION_OPTIONS, `navigate:${selectedActionTab(formData.action)}`)]}
                      onSelectionChange={(keys) => {
                        const key = String(Array.from(keys)[0] || 'navigate:inicio');
                        setFormData((f) => ({
                          ...f,
                          action: key === 'openUrl'
                            ? normalizeUrlAction(actionUrl(f.action))
                            : key === 'openPopup'
                              ? normalizePopupAction(actionPopupId(f.action))
                              : key
                        }));
                      }}
                    >
                      {BANNER_ACTION_OPTIONS.map((a) => <SelectItem key={a.key}>{a.label}</SelectItem>)}
                    </Select>
                  )}
                  {isOpenUrlAction(formData.action) && (
                    <Input
                      label={formData.hasPopup ? 'URL CTA popup' : 'URL CTA banner'}
                      placeholder="https://tikcontrol.live/..."
                      value={actionUrl(formData.action)}
                      onValueChange={(url) => setFormData((f) => ({ ...f, action: normalizeUrlAction(url) }))}
                    />
                  )}

                  {isOpenPopupAction(formData.action) && (
                    <Select
                      label={formData.hasPopup ? 'Popup destino del CTA' : 'Popup destino del banner'}
                      placeholder="Selecciona un popup"
                      selectedKeys={actionPopupId(formData.action) ? [actionPopupId(formData.action)] : []}
                      onSelectionChange={(keys) => {
                        const popupId = String(Array.from(keys)[0] || '');
                        setFormData((f) => ({ ...f, action: normalizePopupAction(popupId) }));
                      }}
                    >
                      {items
                        .filter((item) => item.id !== (editingId || formData.id))
                        .filter((item) => !isArchivedPopup(item) || item.id === actionPopupId(formData.action))
                        .map((item) => (
                          <SelectItem key={item.id}>{item.banner?.title || item.id}</SelectItem>
                        ))}
                    </Select>
                  )}
                </div>

                <div className="rounded-xl border border-default-200 p-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold">Publicacion</h3>
                      <p className="text-xs text-default-500">Dejalo para admins mientras esta en edicion. Al desactivar el switch se publicara para todos los usuarios.</p>
                    </div>
                    <Switch
                      isSelected={formData.hasPopup}
                      onValueChange={(hasPopup) => setFormData((f) => ({
                        ...f,
                        hasPopup,
                        action: hasPopup || isOpenUrlAction(f.action) || isOpenPopupAction(f.action)
                          ? f.action
                          : `navigate:${selectedActionTab(f.action)}`
                      }))}
                    >
                      Con popup
                    </Switch>
                    <Switch isSelected={formData.adminOnly} onValueChange={(adminOnly) => setFormData((f) => ({ ...f, adminOnly }))}>
                      Solo admins
                    </Switch>
                    <Switch isSelected={formData.active} onValueChange={(active) => setFormData((f) => ({ ...f, active }))}>
                      Activo
                    </Switch>
                  </div>
                </div>

                <div className="rounded-xl border border-default-200 p-4">
                  <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold">Audiencia</h3>
                      <p className="text-xs text-default-500">
                        Sin seleccion = todos los usuarios. Dentro de un campo se suma (ES + EN); entre campos se exige todo (idioma Y plan).
                      </p>
                    </div>
                    <div className="text-right">
                      {audienceCountLoading ? (
                        <span className="flex items-center gap-2 text-sm text-default-500">
                          <Spinner size="sm" /> Calculando alcance...
                        </span>
                      ) : audienceCount?.error ? (
                        <span className="text-xs text-danger-600">No se pudo calcular el alcance: {audienceCount.error}</span>
                      ) : audienceCount ? (
                        <div>
                          <div className="text-sm font-semibold">
                            {Number(audienceCount.matching || 0).toLocaleString('es-ES')} de {Number(audienceCount.total || 0).toLocaleString('es-ES')} usuarios
                          </div>
                          {Number(audienceCount.unknownLanguage) > 0 && (
                            <div className="text-xs text-warning-600">
                              +{Number(audienceCount.unknownLanguage).toLocaleString('es-ES')} cumplen todo pero no tienen idioma guardado
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <Select
                      label="Planes"
                      selectionMode="multiple"
                      placeholder="Todos los planes"
                      selectedKeys={new Set(formData.audiencePlans)}
                      onSelectionChange={(keys) => setFormData((f) => ({ ...f, audiencePlans: Array.from(keys).map(String) }))}
                    >
                      {AUDIENCE_PLAN_OPTIONS.map((option) => <SelectItem key={option.key}>{option.label}</SelectItem>)}
                    </Select>
                    <Select
                      label="Idiomas"
                      selectionMode="multiple"
                      placeholder="Todos los idiomas"
                      selectedKeys={new Set(formData.audienceLanguages)}
                      onSelectionChange={(keys) => setFormData((f) => ({ ...f, audienceLanguages: Array.from(keys).map(String) }))}
                    >
                      {TRANSLATION_LANGS.map((lang) => <SelectItem key={lang.key}>{`${lang.label} - ${lang.name}`}</SelectItem>)}
                    </Select>
                    <AudienceTriSelect
                      label="Cuenta TikTok"
                      value={formData.audienceTiktok}
                      options={AUDIENCE_TIKTOK_OPTIONS}
                      onChange={(audienceTiktok) => setFormData((f) => ({ ...f, audienceTiktok }))}
                    />
                    <AudienceTriSelect
                      label="Founders"
                      value={formData.audienceFounder}
                      options={AUDIENCE_FOUNDER_OPTIONS}
                      onChange={(audienceFounder) => setFormData((f) => ({ ...f, audienceFounder }))}
                    />
                    <AudienceTriSelect
                      label="Agencia"
                      value={formData.audienceAgency}
                      options={AUDIENCE_AGENCY_OPTIONS}
                      onChange={(audienceAgency) => setFormData((f) => ({ ...f, audienceAgency }))}
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label="Alta desde"
                        type="date"
                        value={formData.audienceRegisteredAfter}
                        onValueChange={(audienceRegisteredAfter) => setFormData((f) => ({ ...f, audienceRegisteredAfter }))}
                      />
                      <Input
                        label="Alta hasta"
                        type="date"
                        value={formData.audienceRegisteredBefore}
                        onValueChange={(audienceRegisteredBefore) => setFormData((f) => ({ ...f, audienceRegisteredBefore }))}
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-col gap-1.5" data-audience-whitelist>
                    <Textarea
                      label="Lista blanca (opcional)"
                      minRows={2}
                      maxRows={8}
                      placeholder={'ana@correo.com\n@usuario_tiktok\nUID de la cuenta'}
                      value={formData.audienceWhitelist}
                      onValueChange={(audienceWhitelist) => setFormData((f) => ({ ...f, audienceWhitelist, audienceWhitelistLoaded: true }))}
                      description="Un correo, un @usuario de TikTok o un UID por línea. Con lista, el banner (y su popup) solo les sale a ellos, si además cumplen lo de arriba. El equipo de TikControl lo ve siempre."
                    />
                    {whitelistParsed.entries.length > 0 && (
                      <p className="text-xs text-default-500">
                        {whitelistParsed.entries.length} {whitelistParsed.entries.length === 1 ? 'entrada' : 'entradas'}
                        {audienceCount?.whitelist ? ` · ${audienceCount.whitelist.found} encontradas en las cuentas` : ''}
                      </p>
                    )}
                    {audienceCount?.whitelist?.missing?.length > 0 && (
                      <p className="text-xs text-warning-600">
                        Sin cuenta que coincida: {audienceCount.whitelist.missing.join(', ')}
                        {audienceCount.whitelist.total - audienceCount.whitelist.found > audienceCount.whitelist.missing.length
                          ? ` y ${(audienceCount.whitelist.total - audienceCount.whitelist.found - audienceCount.whitelist.missing.length).toLocaleString('es-ES')} más`
                          : ''}
                      </p>
                    )}
                    {whitelistParsed.invalid.length > 0 && (
                      <p className="text-xs text-danger-600">
                        No es un correo, un @usuario ni un UID: {whitelistParsed.invalid.slice(0, 10).join(', ')}
                      </p>
                    )}
                    {!formData.audienceWhitelistLoaded && formData.audienceWhitelistHashes.length > 0 && (
                      <p className="text-xs text-warning-600">
                        Este banner ya tiene una lista de {formData.audienceWhitelistHashes.length} {formData.audienceWhitelistHashes.length === 1 ? 'entrada' : 'entradas'}, pero no se ha podido leer su texto.
                        Si lo dejas vacío se conserva; si escribes otra lista, la sustituye.
                      </p>
                    )}
                    {(whitelistParsed.entries.length > 0 || (!formData.audienceWhitelistLoaded && formData.audienceWhitelistHashes.length > 0)) && (
                      <p className="text-xs text-default-400">
                        En el banner solo se guardan huellas de cada entrada, no los correos ni los @ (el banner lo puede leer cualquier usuario).
                        Las versiones anteriores de la app no entienden la lista: allí solo lo verá el equipo de TikControl.
                        {formData.adminOnly ? ' Con «Solo admins» encendido manda eso: la lista no se usa hasta que lo apagues.' : ''}
                      </p>
                    )}
                  </div>
                  <p className="mt-3 text-xs text-default-400">
                    El alcance es una estimacion con los datos de la nube (el idioma es el del registro de la cuenta y puede faltar en cuentas antiguas).
                    La app decide al momento con el estado real de cada usuario: idioma actual de la interfaz, plan vigente y TikTok vinculado.
                  </p>
                </div>

              </div>

              {formData.hasPopup && (
                <CanvasEditor
                  cfg={MODAL_EDITOR}
                  formData={formData}
                  previewUrls={previewUrls}
                  lang={previewLang}
                  setFormData={setFormData}
                  uploadingField={uploadingField}
                  onImageChange={setImageField}
                  onUpload={uploadAsset}
                />
              )}
            </div>

            {/* Banner vertical + version pequeña: solo para versiones de la app sin el nuevo Inicio. Se sigue guardando igual. */}
            <details className="rounded-xl border border-default-200 p-4">
              <summary className="cursor-pointer text-sm font-semibold">
                Formato anterior del banner (versiones de la app sin el nuevo Inicio)
              </summary>
              <p className="mt-2 text-xs text-default-500">
                Solo lo muestran las versiones de la app anteriores al nuevo Inicio (banner vertical y su versión pequeña); puedes dejarlo como está.
              </p>
              <div className="mt-4 grid gap-5 xl:grid-cols-[minmax(0,1fr)_460px]">
                <div className="flex flex-col gap-4">
                  <div className="rounded-xl border border-default-200 p-4">
                    <h3 className="mb-3 text-sm font-semibold">Imagenes en R2 (formato anterior)</h3>
                    <div className="grid gap-3 lg:grid-cols-2">
                      <AssetField
                        label="Banner grande ES"
                        value={formData.bannerBackgroundEs}
                        onChange={(bannerBackgroundEs) => setImageField('bannerBackgroundEs', bannerBackgroundEs)}
                        uploading={uploadingField === 'bannerBackgroundEs'}
                        onUpload={(file) => uploadAsset('bannerBackgroundEs', 'banner', 'es', file)}
                      />
                      <AssetField
                        label="Banner grande EN"
                        value={formData.bannerBackgroundEn}
                        onChange={(bannerBackgroundEn) => setImageField('bannerBackgroundEn', bannerBackgroundEn)}
                        uploading={uploadingField === 'bannerBackgroundEn'}
                        onUpload={(file) => uploadAsset('bannerBackgroundEn', 'banner', 'en', file)}
                      />
                      <AssetField
                        label="Banner pequeno ES"
                        value={formData.bannerCompactBackgroundEs}
                        onChange={(bannerCompactBackgroundEs) => setImageField('bannerCompactBackgroundEs', bannerCompactBackgroundEs)}
                        uploading={uploadingField === 'bannerCompactBackgroundEs'}
                        onUpload={(file) => uploadAsset('bannerCompactBackgroundEs', 'banner-compact', 'es', file)}
                      />
                      <AssetField
                        label="Banner pequeno EN"
                        value={formData.bannerCompactBackgroundEn}
                        onChange={(bannerCompactBackgroundEn) => setImageField('bannerCompactBackgroundEn', bannerCompactBackgroundEn)}
                        uploading={uploadingField === 'bannerCompactBackgroundEn'}
                        onUpload={(file) => uploadAsset('bannerCompactBackgroundEn', 'banner-compact', 'en', file)}
                      />
                    </div>
                  </div>

                  <LayersControls
                    label="Texto HTML sobre banner grande"
                    layers={formData.bannerLayers}
                    fallback={DEFAULT_LAYER}
                    importLabel="Importar textos del banner pequeno"
                    importDisabled={!layersHaveText(formData.compactLayers)}
                    onImport={() => importBannerTexts('compactLayers', 'bannerLayers', DEFAULT_LAYER)}
                    onChange={(bannerLayers) => setFormData((f) => ({ ...f, bannerLayers }))}
                  />
                  <LayersControls
                    label="Texto HTML sobre banner pequeno"
                    layers={formData.compactLayers}
                    fallback={DEFAULT_COMPACT_LAYER}
                    importLabel="Importar textos del banner grande"
                    importDisabled={!layersHaveText(formData.bannerLayers)}
                    onImport={() => importBannerTexts('bannerLayers', 'compactLayers', DEFAULT_COMPACT_LAYER)}
                    onChange={(compactLayers) => setFormData((f) => ({ ...f, compactLayers }))}
                  />
                  <CtaStyleControls
                    label="Boton banner grande"
                    style={formData.bannerCtaStyle}
                    onChange={(bannerCtaStyle) => setFormData((f) => ({ ...f, bannerCtaStyle }))}
                  />
                  <CtaStyleControls
                    label="Boton banner pequeno"
                    style={formData.compactCtaStyle}
                    onChange={(compactCtaStyle) => setFormData((f) => ({ ...f, compactCtaStyle }))}
                  />
                </div>

                <LegacyBannerPreview formData={formData} previewUrls={previewUrls} lang={previewLang} setFormData={setFormData} />
              </div>
            </details>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={form.onClose} isDisabled={saving}>Cancelar</Button>
            <Button color="primary" onPress={saveForm} isLoading={saving}>{editingId ? 'Guardar' : 'Crear'}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={translationDialog.isOpen} onClose={translationDialog.onClose} size="5xl" scrollBehavior="inside">
        <ModalContent>
          <ModalHeader>Traducciones con ChatGPT</ModalHeader>
          <ModalBody className="flex flex-col gap-4">
            <div className="rounded-xl border border-default-200 p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold">Prompt</h3>
                  <p className="text-xs text-default-500">Copia esto en ChatGPT y pide la respuesta en JSON.</p>
                </div>
                <Button size="sm" variant="flat" onPress={copyTranslationPrompt}>Copiar prompt</Button>
              </div>
              <Textarea
                value={translationPrompt}
                minRows={12}
                readOnly
                classNames={{ input: 'font-mono text-xs' }}
              />
            </div>
            <div className="rounded-xl border border-default-200 p-4">
              <h3 className="mb-3 text-sm font-semibold">Respuesta JSON de ChatGPT</h3>
              <Textarea
                placeholder='Pega aqui el JSON, por ejemplo {"translations":{"bannerCta":{"en":"View"}}}'
                value={translationJson}
                onValueChange={(value) => {
                  setTranslationJson(value);
                  setTranslationError('');
                }}
                minRows={10}
                classNames={{ input: 'font-mono text-xs' }}
              />
              {translationError && (
                <div className="mt-3 rounded-md border border-danger-200 bg-danger-50 p-3 text-xs text-danger-700">
                  {translationError}
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={translationDialog.onClose}>Cerrar</Button>
            <Button color="primary" onPress={applyPastedTranslations}>Aplicar traducciones</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
